<template>
  <div class="restaurante-salao">
    <div class="card card-custom gutter-b">
      <div class="card-header border-0 py-5">
        <h3 class="card-title align-items-start flex-column">
          <span class="card-label font-weight-bolder text-dark">
            {{ titulo }}
          </span>
          <span class="text-muted mt-2 font-weight-bold font-size-sm">
            {{ subtitulo }}
          </span>
        </h3>
        <div class="card-toolbar">
          <div class="restaurante-visao-seletor mr-3" role="group" aria-label="Visão do salão">
            <button
              type="button"
              class="btn btn-sm font-weight-bold"
              :class="
                visualizacaoOperacao === 'mesas'
                  ? 'btn-primary'
                  : 'btn-light'
              "
              @click="trocarVisualizacaoOperacao('mesas')"
            >
              Mesas
            </button>
            <button
              type="button"
              class="btn btn-sm font-weight-bold"
              :class="
                visualizacaoOperacao === 'comandas'
                  ? 'btn-primary'
                  : 'btn-light'
              "
              @click="trocarVisualizacaoOperacao('comandas')"
            >
              Comandas
            </button>
          </div>
          <button
            v-if="!modoApenasComandas"
            class="btn btn-light-primary font-weight-bolder font-size-sm mr-2"
            type="button"
            @click="openReservasModal"
          >
            Reservas
          </button>
          <router-link
            v-if="canShowGestaoShortcuts"
            class="btn btn-light-dark font-weight-bolder font-size-sm mr-2"
            :to="{ name: 'restauranteProducao' }"
          >
            Produção
          </router-link>
          <router-link
            v-if="canShowGestaoShortcuts"
            class="btn btn-light-dark font-weight-bolder font-size-sm mr-2"
            :to="{ name: 'restauranteMesasCadastro' }"
          >
            Cadastro
          </router-link>
          <button
            v-if="canShowQrMesa"
            class="btn btn-light-primary font-weight-bolder font-size-sm mr-2"
            type="button"
            :disabled="!featureFlags.qrMesa"
            v-b-tooltip.hover.bottom="
              featureFlags.qrMesa
                ? 'Gerenciar QR Codes'
                : 'QR Code por mesa será liberado por feature flag'
            "
          >
            QR Mesa
          </button>
          <button
            v-if="canShowComandaAvulsa"
            class="btn btn-light-primary font-weight-bolder font-size-sm mr-2"
            type="button"
            @click="openComandaAvulsaModal()"
          >
            {{ comandaAvulsaActionLabel }}
          </button>
          <router-link
            v-if="canShowPedidosOnlineShortcut"
            class="btn btn-light-warning font-weight-bolder font-size-sm mr-2"
            :to="{ name: 'restaurantePedidosOnline' }"
          >
            Pedidos online
          </router-link>
          <button
            class="btn btn-dark font-weight-bolder font-size-sm"
            @click="refreshOperacao()"
          >
            Atualizar
          </button>
        </div>
      </div>

      <div class="card-body pt-0">
        <div class="operacao-resumo">
          <div
            v-for="metric in operacaoMetrics"
            :key="metric.label"
            class="operacao-metric"
          >
            <span>{{ metric.label }}</span>
            <strong>{{ metric.value }}</strong>
            <small>{{ metric.hint }}</small>
          </div>
        </div>

        <div v-if="canShowEntregaQueue" class="entrega-queue">
          <div class="entrega-queue-header">
            <div>
              <h5>Prontos para entrega</h5>
              <span>
                Itens finalizados pela cozinha/bar aguardando baixa do salão
              </span>
            </div>
            <div class="entrega-queue-actions">
              <strong>{{ entregasProntas.length }} item(ns)</strong>
              <button
                type="button"
                class="btn btn-sm btn-light-dark font-weight-bold"
                @click="getEntregasProntas()"
              >
                Atualizar
              </button>
            </div>
          </div>

          <div v-if="!entregaTickets.length" class="empty-state py-4">
            Nenhum item pronto aguardando entrega.
          </div>

          <div v-else class="entrega-grid">
            <div
              v-for="ticket in entregaTickets"
              :key="ticket.key"
              class="entrega-card entrega-ticket-card"
            >
              <button
                type="button"
                class="entrega-card-top entrega-ticket-toggle"
                @click="toggleEntregaTicket(ticket)"
              >
                <div>
                  <strong>{{ entregaMesaLabel(ticket.firstItem) }}</strong>
                  <span>{{ entregaSetorLabel(ticket.firstItem) }}</span>
                </div>
                <span class="entrega-ticket-summary">
                  {{ ticket.items.length }} item(ns)
                </span>
                <em>{{ entregaTempoLabel(ticket.firstItem) }}</em>
                <i
                  :class="[
                    'fas',
                    isEntregaTicketExpanded(ticket)
                      ? 'fa-chevron-up'
                      : 'fa-chevron-down',
                  ]"
                ></i>
              </button>

              <div class="entrega-meta entrega-ticket-meta">
                <span>{{ entregaTicketLabel(ticket.firstItem) }}</span>
              </div>

              <div
                v-if="isEntregaTicketExpanded(ticket)"
                class="entrega-ticket-items"
              >
                <div
                  v-for="item in ticket.items"
                  :key="item.id"
                  class="entrega-ticket-item"
                >
                  <div>
                    <strong class="entrega-produto">
                      {{ entregaProdutoDescricao(item) }}
                    </strong>

                    <div class="entrega-meta">
                      <span>{{ entregaQuantidadeLabel(item) }}</span>
                      <span>{{ entregaComandaLabel(item) }}</span>
                    </div>

                    <p v-if="item.observacao" class="entrega-observacao">
                      {{ item.observacao }}
                    </p>
                  </div>

                  <button
                    type="button"
                    class="btn btn-sm btn-light-success font-weight-bold"
                    :disabled="isEntregaUpdating(item)"
                    @click="marcarProducaoEntregue(item)"
                  >
                    Entregar item
                  </button>
                </div>
              </div>

              <div class="entrega-actions">
                <button
                  type="button"
                  class="btn btn-sm btn-success font-weight-bold"
                  :disabled="isEntregaTicketUpdating(ticket)"
                  @click="marcarEntregaTicket(ticket)"
                >
                  Entregar tudo
                </button>
                <button
                  type="button"
                  class="btn btn-sm btn-light-dark font-weight-bold"
                  @click="openMesaFromEntrega(ticket.firstItem)"
                >
                  {{ modoApenasComandas ? "Comanda" : "Mesa" }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <template v-if="modoApenasComandas">
          <div class="comandas-operacao">
            <div class="comandas-operacao-toolbar">
              <div class="comandas-search">
                <b-form-input
                  ref="comandasModoSearch"
                  v-model.trim="comandasModoSearch"
                  autocomplete="off"
                  placeholder="Buscar por código ou nome"
                  @keyup.enter="getComandasModo()"
                />
                <button
                  type="button"
                  class="btn btn-light-dark font-weight-bold"
                  :disabled="comandasModoLoading"
                  @click="getComandasModo()"
                >
                  Buscar
                </button>
              </div>

              <div class="comandas-filter-chips">
                <button
                  v-for="filtro in comandasModoFiltros"
                  :key="filtro.value"
                  type="button"
                  :class="[
                    'comandas-filter-chip',
                    { active: comandasModoFiltro === filtro.value },
                  ]"
                  @click="setComandasModoFiltro(filtro.value)"
                >
                  {{ filtro.text }}
                </button>
              </div>
            </div>

            <div class="nova-comanda-inline">
              <b-form-input
                v-model.trim="comandaAvulsaForm.codigo"
                autocomplete="off"
                placeholder="Código"
                @keyup.enter="buscarComandaAvulsa"
              />
              <b-form-input
                v-model.trim="comandaAvulsaForm.nome"
                autocomplete="off"
                placeholder="Nome"
                @keyup.enter="buscarComandaAvulsa"
              />
              <button
                type="button"
                class="btn btn-primary font-weight-bold"
                :disabled="comandaAvulsaLoading"
                @click="buscarComandaAvulsa"
              >
                Nova comanda
              </button>
            </div>

            <div v-if="comandasModoLoading" class="empty-state">
              Carregando comandas abertas.
            </div>

            <div
              v-else-if="!comandasModo.length"
              class="empty-state comandas-empty"
            >
              Nenhuma comanda aberta encontrada.
            </div>

            <div v-else class="comandas-operacao-grid">
              <button
                v-for="comanda in comandasModo"
                :key="comanda.id"
                type="button"
                class="comanda-operacao-card"
                @click="openComandaModo(comanda)"
              >
                <div class="comanda-card-top">
                  <span>{{ comandaEstadoLabel(comanda) }}</span>
                  <strong>{{ comandaModoTitulo(comanda) }}</strong>
                </div>
                <div class="comanda-card-info">
                  <span v-if="comanda.codigo">Código {{ comanda.codigo }}</span>
                  <span>{{ comandaAbertaLabel(comanda) }}</span>
                  <span>{{ comanda.quantidade_itens_ativos || 0 }} item(ns)</span>
                  <span v-if="canShowFinancialInfo">
                    {{ formatMoney(comanda.valor_total || 0) }}
                  </span>
                </div>
                <div class="comanda-card-footer">
                  <span
                    v-for="chip in comandaProducaoChips(comanda)"
                    :key="chip.text"
                    :class="['comanda-prod-chip', chip.class]"
                  >
                    {{ chip.text }}
                  </span>
                  <span class="comanda-card-action">
                    Abrir <i class="fas fa-arrow-right"></i>
                  </span>
                </div>
              </button>
            </div>
          </div>
        </template>

        <template v-else>
        <b-row class="align-items-end mb-6">
          <b-col lg="4" md="5">
            <b-form-group label="Ambiente" label-for="ambiente_id">
              <b-form-select
                id="ambiente_id"
                v-model="ambienteId"
                :options="ambienteOptions"
                @change="refreshOperacao()"
              />
            </b-form-group>
          </b-col>
          <b-col lg="4" md="7">
            <b-form-group label="Buscar mesa" label-for="busca_mesa_salao">
              <b-form-input
                id="busca_mesa_salao"
                v-model.trim="buscaMesaSalao"
                autocomplete="off"
                placeholder="Número, nome ou cliente"
              />
            </b-form-group>
          </b-col>
          <b-col lg="4" md="7">
            <div class="salao-mode-group">
              <button
                type="button"
                :class="[
                  'salao-mode-button',
                  {
                    active: visualizacaoSalao === 'mapa' && !editandoLayout,
                  },
                ]"
                @click="setVisualizacaoSalao('mapa')"
              >
                Mapa
              </button>
              <button
                type="button"
                :class="[
                  'salao-mode-button',
                  { active: visualizacaoSalao === 'grid' },
                ]"
                @click="setVisualizacaoSalao('grid')"
              >
                Grid
              </button>
              <button
                v-if="featureFlags.layoutVisual"
                type="button"
                :class="['salao-mode-button', { active: editandoLayout }]"
                @click="toggleLayoutEditor"
              >
                Layout
              </button>
            </div>
          </b-col>
          <b-col lg="4" md="12">
            <div class="status-legenda">
              <span
                v-for="status in statusList"
                :key="status.value"
                class="status-legenda-item"
              >
                <span :class="['status-dot', `status-dot-${status.value}`]" />
                {{ status.text }}
              </span>
            </div>
          </b-col>
        </b-row>

        <div v-if="!ambientes.length" class="empty-state">
          Nenhuma mesa encontrada para o filtro selecionado.
        </div>

        <div
          v-for="ambiente in ambientes"
          :key="ambiente.id"
          class="ambiente-bloco"
        >
          <div class="ambiente-header">
            <div>
              <h4>{{ ambiente.nome }}</h4>
              <span v-if="ambiente.descricao">{{ ambiente.descricao }}</span>
            </div>
            <div class="ambiente-actions">
              <span class="text-muted font-weight-bold">
                {{ ambiente.mesas.length }} mesa(s)
              </span>
              <button
                v-if="editandoLayout"
                type="button"
                class="btn btn-sm btn-light-dark font-weight-bold"
                @click="autoArrangeAmbiente(ambiente)"
              >
                Reorganizar
              </button>
              <button
                v-if="editandoLayout"
                type="button"
                class="btn btn-sm btn-primary font-weight-bold"
                @click="saveLayoutAmbiente(ambiente)"
              >
                Salvar layout
              </button>
            </div>
          </div>

          <div v-if="editandoLayout" class="layout-editor-toolbar">
            <div class="layout-editor-target">
              <span>Mesa selecionada</span>
              <strong>{{ mesaLayoutSelecionadaLabel(ambiente) }}</strong>
            </div>
            <div class="layout-editor-actions">
              <button
                type="button"
                class="btn btn-icon btn-light-dark btn-sm"
                :disabled="!selectedLayoutMesa(ambiente)"
                title="Mover para a esquerda"
                @click="nudgeSelectedMesa(ambiente, -2, 0)"
              >
                <i class="fas fa-arrow-left"></i>
              </button>
              <button
                type="button"
                class="btn btn-icon btn-light-dark btn-sm"
                :disabled="!selectedLayoutMesa(ambiente)"
                title="Mover para cima"
                @click="nudgeSelectedMesa(ambiente, 0, -2)"
              >
                <i class="fas fa-arrow-up"></i>
              </button>
              <button
                type="button"
                class="btn btn-icon btn-light-dark btn-sm"
                :disabled="!selectedLayoutMesa(ambiente)"
                title="Mover para baixo"
                @click="nudgeSelectedMesa(ambiente, 0, 2)"
              >
                <i class="fas fa-arrow-down"></i>
              </button>
              <button
                type="button"
                class="btn btn-icon btn-light-dark btn-sm"
                :disabled="!selectedLayoutMesa(ambiente)"
                title="Mover para a direita"
                @click="nudgeSelectedMesa(ambiente, 2, 0)"
              >
                <i class="fas fa-arrow-right"></i>
              </button>
              <button
                type="button"
                class="btn btn-icon btn-light-primary btn-sm"
                :disabled="!selectedLayoutMesa(ambiente)"
                title="Diminuir"
                @click="resizeSelectedMesa(ambiente, -1, -1)"
              >
                <i class="fas fa-compress-alt"></i>
              </button>
              <button
                type="button"
                class="btn btn-icon btn-light-primary btn-sm"
                :disabled="!selectedLayoutMesa(ambiente)"
                title="Aumentar"
                @click="resizeSelectedMesa(ambiente, 1, 1)"
              >
                <i class="fas fa-expand-alt"></i>
              </button>
              <button
                type="button"
                class="btn btn-icon btn-light-primary btn-sm"
                :disabled="!selectedLayoutMesa(ambiente)"
                title="Alternar formato"
                @click="toggleSelectedMesaShape(ambiente)"
              >
                <i class="fas fa-circle"></i>
              </button>
            </div>
          </div>

          <div
            v-if="visualizacaoSalao === 'mapa' && featureFlags.layoutVisual"
            class="salao-map"
          >
            <svg class="mesa-vinculos-svg" aria-hidden="true">
              <line
                v-for="vinculo in mesaVinculosLayout(ambiente)"
                :key="vinculo.key"
                :x1="`${vinculo.x1}%`"
                :y1="`${vinculo.y1}%`"
                :x2="`${vinculo.x2}%`"
                :y2="`${vinculo.y2}%`"
              />
            </svg>
            <button
              v-for="(mesa, index) in mesasAmbienteFiltradas(ambiente)"
              :key="mesa.id"
              type="button"
              :class="[
                'mesa-map-card',
                `mesa-map-card-${mesa.status}`,
                {
                  'mesa-map-card-layout': editandoLayout,
                  'mesa-map-card-selected': mesaLayoutSelecionadaId === mesa.id,
                  'mesa-map-card-dragging':
                    layoutDrag && layoutDrag.mesaId === mesa.id,
                  'mesa-map-card-round': mesaShape(mesa) === 'round',
                  'mesa-map-card-linked': mesaTemVinculo(mesa),
                  'mesa-map-card-reserva-atual': mesa.reserva_atual,
                },
              ]"
              :style="mesaMapStyle(mesa, index, ambiente)"
              @click="handleMesaMapClick(mesa)"
              @pointerdown="startLayoutDrag($event, mesa, ambiente)"
            >
              <i
                v-if="mesaTemVinculo(mesa)"
                class="fas fa-link mesa-link-icon"
              ></i>
              <span>{{ statusLabel(mesa.status) }}</span>
              <strong>{{ mesa.numero }}</strong>
              <small v-if="mesaVinculoResumo(mesa, true)" class="mesa-link-label">
                {{ mesaVinculoResumo(mesa, true) }}
              </small>
              <small
                v-if="mesaReservaResumo(mesa, true)"
                class="mesa-reserva-info"
              >
                {{ mesaReservaResumo(mesa, true) }}
              </small>
              <small v-if="mesaResumoProducao(mesa)">
                {{ mesaResumoProducao(mesa) }}
              </small>
              <small v-else>{{ mesaCapacidadeLabel(mesa) }}</small>
              <em v-if="canShowFinancialInfo && mesaValorPendente(mesa) > 0">
                {{ formatMoney(mesaValorPendente(mesa)) }}
              </em>
            </button>
          </div>

          <div v-else class="mesas-grid">
            <button
              v-for="mesa in mesasAmbienteFiltradas(ambiente)"
              :key="mesa.id"
              type="button"
              :class="[
                'mesa-card',
                `mesa-card-${mesa.status}`,
                { 'mesa-card-reserva-atual': mesa.reserva_atual },
              ]"
              @click="handleMesaClick(mesa)"
            >
              <div class="mesa-card-topline">
                <span class="mesa-status">{{ statusLabel(mesa.status) }}</span>
                <span v-if="mesaTemVinculo(mesa)" class="mesa-link-chip">
                  <i class="fas fa-link"></i>
                  {{ mesaVinculoResumo(mesa) }}
                </span>
              </div>
              <strong>Mesa {{ mesa.numero }}</strong>
              <small v-if="mesa.nome">{{ mesa.nome }}</small>
              <span v-if="mesa.descricao" class="mesa-descricao">
                {{ mesa.descricao }}
              </span>

              <div class="mesa-info">
                <span>{{ mesaCapacidadeLabel(mesa) }}</span>
                <span v-if="mesaGarcomLabel(mesa)">
                  {{ mesaGarcomLabel(mesa) }}
                </span>
                <span v-if="mesa.atendimento_aberto_id">
                  Atendimento #{{ mesa.atendimento_aberto_id }}
                </span>
                <span v-if="mesa.tempo_aberto">
                  {{ mesa.tempo_aberto }}
                </span>
                <span v-if="mesa.quantidade_itens !== null">
                  {{ mesa.quantidade_itens }} item(ns)
                </span>
                <span
                  v-if="
                    canShowFinancialInfo &&
                    mesa.valor_total_atendimento !== null
                  "
                >
                  {{ formatMoney(mesa.valor_total_atendimento) }}
                </span>
                <span
                  v-if="canShowFinancialInfo && mesaValorPendente(mesa) > 0"
                >
                  Pendente {{ formatMoney(mesaValorPendente(mesa)) }}
                </span>
                <span v-if="mesaResumoProducao(mesa)">
                  {{ mesaResumoProducao(mesa) }}
                </span>
                <span v-if="mesaReservaResumo(mesa)" class="mesa-reserva-info">
                  {{ mesaReservaResumo(mesa) }}
                </span>
              </div>
            </button>
          </div>
        </div>
        </template>
      </div>
    </div>

    <b-modal
      id="modalAbrirAtendimentoRestaurante"
      title="Abrir atendimento"
      centered
      hide-footer
    >
      <div v-if="mesaParaAbrir">
        <p class="modal-atendimento-text">
          Mesa <strong>{{ mesaParaAbrir.numero }}</strong>
        </p>

        <div v-if="reservaAtualMesaParaAbrir" class="reserva-chegada-box">
          <strong>Reserva de {{ reservaAtualMesaParaAbrir.nome }}</strong>
          <span>
            {{ formatReservaHorario(reservaAtualMesaParaAbrir.horario) }}
            -
            {{ formatReservaHorario(reservaAtualMesaParaAbrir.horario_fim) }}
            ·
            {{ reservaAtualMesaParaAbrir.quantidade_pessoas || "-" }}
            pessoa(s)
          </span>
          <small>Confirme o nome de quem chegou para abrir a comanda.</small>
        </div>

        <b-form-group
          v-if="!reservaAtualMesaParaAbrir"
          label="Primeira comanda"
        >
          <b-form-radio-group
            v-model="aberturaAtendimentoForm.tipo"
            :options="aberturaAtendimentoTipos"
            buttons
            button-variant="outline-primary"
            class="w-100 abertura-comanda-toggle"
            @change="focusAberturaComandaInput"
          />
        </b-form-group>

        <b-form-group
          v-if="aberturaAtendimentoForm.tipo === 'nome'"
          :label="
            reservaAtualMesaParaAbrir
              ? 'Nome de quem chegou'
              : 'Nome da comanda'
          "
          label-for="abertura_comanda_nome"
        >
          <b-form-input
            id="abertura_comanda_nome"
            ref="aberturaComandaNome"
            v-model.trim="aberturaAtendimentoForm.nome"
            autocomplete="off"
            :placeholder="
              reservaAtualMesaParaAbrir
                ? 'Confirme ou ajuste o nome'
                : 'Nome do cliente ou identificação'
            "
            @keyup.enter="openAtendimento"
          />
        </b-form-group>

        <b-form-group
          v-else
          label="Código da comanda"
          label-for="abertura_comanda_codigo"
        >
          <b-form-input
            id="abertura_comanda_codigo"
            ref="aberturaComandaCodigo"
            v-model.trim="aberturaAtendimentoForm.codigo"
            autocomplete="off"
            placeholder="Digite ou leia o código"
            @keyup.enter="openAtendimento"
          />
        </b-form-group>

        <div class="abrir-atendimento-juncao">
          <div>
            <strong>Juntar esta mesa</strong>
            <span>Vincular a uma mesa mãe sem criar nova comanda</span>
          </div>
          <b-form-select
            v-model="juntarMesaAberturaForm.mesa_destino_id"
            :options="mesaJuncaoAberturaOptions"
          />
          <button
            type="button"
            class="btn btn-light-dark font-weight-bold"
            :disabled="juntandoMesaAbertura"
            @click="juntarMesaNaAbertura"
          >
            Juntar mesa
          </button>
        </div>

        <div class="modal-actions">
          <button
            type="button"
            class="btn btn-clean font-weight-bold"
            @click="$bvModal.hide('modalAbrirAtendimentoRestaurante')"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="btn btn-primary font-weight-bold"
            :disabled="abrindoAtendimento"
            @click="openAtendimento"
          >
            Abrir atendimento
          </button>
        </div>
      </div>
    </b-modal>

    <b-modal
      id="modalUtilizarReservaRestaurante"
      :title="
        mesaParaAbrirReserva
          ? 'Reservas da mesa ' + mesaParaAbrirReserva.numero
          : 'Reservas'
      "
      centered
      no-close-on-backdrop
      hide-footer
    >
      <div v-if="reservasParaUtilizar.length" class="utilizar-reserva-content">
        <p class="text-muted mb-3">
          Esta mesa possui reserva(ões). Deseja utilizar uma reserva
          (vinculando ao cliente) ou abrir a mesa normalmente mantendo as
          reservas?
        </p>

        <div v-if="reservasParaUtilizar.length > 1" class="reserva-lista">
          <label class="font-weight-bold mb-2">Selecione o cliente:</label>
          <b-form-radio-group
            v-model="reservaUtilizarSelecionada"
            class="w-100"
            stacked
          >
            <b-form-radio
              v-for="reserva in reservasParaUtilizar"
              :key="reserva.id"
              :value="reserva"
              class="reserva-radio mb-2"
            >
              <div class="d-flex flex-wrap align-items-center">
                <strong class="mr-2">{{ reserva.nome || "—" }}</strong>
                <small class="text-muted">
                  {{ formatReservaHorario(reserva.horario)
                  }}<span v-if="reserva.horario_fim">
                    às {{ formatReservaHorario(reserva.horario_fim) }}</span
                  >
                  <span v-if="reserva.quantidade_pessoas">
                    · {{ reserva.quantidade_pessoas }} pessoa(s)</span
                  >
                </small>
              </div>
            </b-form-radio>
          </b-form-radio-group>
        </div>

        <div v-else class="reserva-unica-info mb-3">
          <strong>{{ reservasParaUtilizar[0]?.nome || "—" }}</strong>
          <small class="text-muted d-block">
            {{ formatReservaHorario(reservasParaUtilizar[0]?.horario)
            }}<span v-if="reservasParaUtilizar[0]?.horario_fim">
              às
              {{ formatReservaHorario(reservasParaUtilizar[0]?.horario_fim) }}</span
            >
          </small>
        </div>

        <div
          class="d-flex flex-column flex-md-row justify-content-between gap-2 mt-4"
        >
          <button
            type="button"
            class="btn btn-light font-weight-bold"
            @click="abrirSemUsarReserva"
          >
            Não usar / abrir normalmente
          </button>
          <button
            type="button"
            class="btn btn-primary font-weight-bold"
            :disabled="
              reservasParaUtilizar.length > 1 && !reservaUtilizarSelecionada
            "
            @click="confirmarUtilizarReserva"
          >
            Utilizar reserva
          </button>
        </div>
      </div>
      <div v-else class="text-center text-muted py-4">
        Nenhuma reserva encontrada.
      </div>
    </b-modal>

    <b-modal
      id="modalReservasRestaurante"
      title="Reservas"
      centered
      hide-footer
      size="lg"
      @shown="getReservas"
    >
      <div class="reservas-modal">
        <b-row class="align-items-end">
          <b-col lg="5" md="6">
            <b-form-group label="Nome" label-for="reserva_nome">
              <b-form-input
                id="reserva_nome"
                v-model.trim="reservaForm.nome"
                autocomplete="off"
                placeholder="Nome da reserva"
              />
            </b-form-group>
          </b-col>
          <b-col lg="3" md="6">
            <b-form-group label="Data" label-for="reserva_data">
              <b-form-input
                id="reserva_data"
                v-model="reservaForm.data"
                type="date"
                @change="getReservas"
              />
            </b-form-group>
          </b-col>
          <b-col lg="2" md="6">
            <b-form-group label="Início" label-for="reserva_horario">
              <b-form-input
                id="reserva_horario"
                v-model="reservaForm.horario"
                type="time"
              />
            </b-form-group>
          </b-col>
          <b-col lg="2" md="6">
            <b-form-group label="Fim" label-for="reserva_horario_fim">
              <b-form-input
                id="reserva_horario_fim"
                v-model="reservaForm.horario_fim"
                type="time"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row class="align-items-end">
          <b-col lg="3" md="6">
            <b-form-group label="Pessoas" label-for="reserva_pessoas">
              <b-form-input
                id="reserva_pessoas"
                v-model.number="reservaForm.quantidade_pessoas"
                min="1"
                type="number"
              />
            </b-form-group>
          </b-col>
          <b-col lg="5" md="6">
            <div class="reserva-selecao-resumo">
              <strong>{{ reservaMesasSelecionadasResumo }}</strong>
              <span>{{ reservaCapacidadeSelecionada }} lugar(es)</span>
            </div>
          </b-col>
          <b-col lg="4" md="12">
            <button
              type="button"
              class="btn btn-primary btn-block font-weight-bold"
              :disabled="reservasLoading"
              @click="createReserva"
            >
              Criar reserva
            </button>
          </b-col>
        </b-row>

        <div class="reserva-mesas-picker">
          <div class="itens-atendimento-header">
            <h5>Mesas da reserva</h5>
            <span>{{ reservaForm.mesa_ids.length }} selecionada(s)</span>
          </div>
          <div class="reserva-mesas-map">
            <button
              v-for="mesa in reservaMesasMapa"
              :key="mesa.id"
              type="button"
              :disabled="mesaReservaIndisponivel(mesa)"
              :class="[
                'reserva-mesa-card',
                `reserva-mesa-card-${mesa.status}`,
                {
                  'reserva-mesa-card-selected': isMesaSelecionadaReserva(mesa),
                  'reserva-mesa-card-busy': mesaReservaIndisponivel(mesa),
                },
              ]"
              @click="toggleMesaReserva(mesa)"
            >
              <span>{{ mesa.ambiente_nome }}</span>
              <strong>{{ mesa.numero }}</strong>
              <small>{{ mesaCapacidadeLabel(mesa) }}</small>
              <em v-if="mesaReservaIndisponivel(mesa)">
                {{ mesaReservaConflitoLabel(mesa) }}
              </em>
            </button>
          </div>
        </div>

        <div class="reservas-list">
          <div class="itens-atendimento-header">
            <h5>Próximas reservas</h5>
            <button
              type="button"
              class="btn btn-sm btn-light-dark font-weight-bold"
              :disabled="reservasLoading"
              @click="getReservas"
            >
              Atualizar
            </button>
          </div>
          <div v-if="!reservas.length" class="empty-state py-4">
            Nenhuma reserva futura cadastrada.
          </div>
          <div v-else class="reservas-list-items">
            <div
              v-for="reserva in reservas"
              :key="reserva.id"
              class="reserva-row"
            >
              <div>
                <strong>{{ reserva.nome }}</strong>
                <span>
                  {{ formatReservaData(reserva.data) }} às
                  {{ formatReservaHorario(reserva.horario) }} -
                  {{ formatReservaHorario(reserva.horario_fim) }}
                </span>
              </div>
              <span>{{ reservaMesaLabel(reserva) }}</span>
              <span>{{ reserva.quantidade_pessoas || "-" }} pessoa(s)</span>
              <button
                v-if="reserva.status === 'ativa'"
                type="button"
                class="btn btn-sm btn-light-danger font-weight-bold"
                :disabled="reservasLoading"
                @click="cancelReserva(reserva)"
              >
                Cancelar
              </button>
              <span v-else class="label label-light-danger label-inline">
                Cancelada
              </span>
            </div>
          </div>
        </div>
      </div>
    </b-modal>

    <b-modal
      id="modalAtendimentoRestaurante"
      title="Atendimento da mesa"
      centered
      hide-footer
      no-enforce-focus
      size="xl"
      dialog-class="restaurante-atendimento-dialog"
      content-class="restaurante-atendimento-content"
      body-class="atendimento-workspace-modal"
    >
      <div v-if="mesaSelecionada" class="atendimento-shell">
        <div class="modal-atendimento-header">
          <strong>{{ mesaDisplayLabel(mesaSelecionada) }}</strong>
          <span :class="statusBadgeClass(mesaSelecionada.status)">
            {{ statusLabel(mesaSelecionada.status) }}
          </span>
        </div>
        <div class="modal-atendimento-info">
          <p v-if="mesaSelecionada.descricao">
            {{ mesaSelecionada.descricao }}
          </p>
          <p v-if="mesaSelecionada.cliente_nome">
            Cliente:
            <strong>{{ mesaSelecionada.cliente_nome }}</strong>
          </p>
          <p>
            Atendimento:
            <strong>#{{ mesaSelecionada.atendimento_aberto_id }}</strong>
          </p>
          <p>
            Tempo aberto:
            <strong>{{ mesaSelecionada.tempo_aberto || "-" }}</strong>
          </p>
          <p>
            Itens:
            <strong>{{ mesaSelecionada.quantidade_itens || 0 }}</strong>
          </p>
          <p v-if="canShowFinancialInfo">
            Total:
            <strong>
              {{ formatMoney(mesaSelecionada.valor_total_atendimento || 0) }}
            </strong>
          </p>
        </div>

        <div class="comandas-atendimento mb-6">
          <div class="itens-atendimento-header">
            <h5>Comandas</h5>
            <div class="d-flex flex-wrap">
              <button
                type="button"
                class="btn btn-light-primary btn-sm font-weight-bold mr-2"
                @click="openComandaAvulsaModal(true)"
              >
                Adicionar avulsa
              </button>
              <button
                type="button"
                class="btn btn-light-primary btn-sm font-weight-bold"
                @click="openNovaComandaModal"
              >
                Nova comanda
              </button>
            </div>
          </div>
          <div v-if="!comandas.length" class="empty-state py-4">
            Nenhuma comanda vinculada ao atendimento.
          </div>
          <div v-else class="comandas-grid">
            <button
              v-for="comanda in comandas"
              :key="comanda.id"
              type="button"
              :class="[
                'comanda-card',
                { 'comanda-card-active': comandaAtivaId === comanda.id },
              ]"
              @click="setComandaAtiva(comanda.id)"
            >
              <strong>{{ comandaLabel(comanda) }}</strong>
              <span>{{
                comanda.pessoa ? comanda.pessoa.nome : "Sem cliente"
              }}</span>
              <span>{{ comandaResumoLabel(comanda) }}</span>
            </button>
          </div>
        </div>

        <div class="produto-atendimento-form">
          <SeletorProdutosGarcom
            ref="seletorProdutosGarcom"
            :quantidades="quantidadesAdicionadasSessao"
            @quick-add="onQuickAddProdutoGarcom"
            @select="onSelectProdutoGarcom"
          />
          <b-row class="align-items-end mt-3">
            <b-col lg="3" md="12">
              <b-form-group label="Produto" label-for="produto_atendimento">
                <VueSelect
                  id="produto_atendimento"
                  v-model="produtoSelecionado"
                  :filterable="false"
                  :options="produtos"
                  label="descricao"
                  placeholder="Buscar produto"
                  @search="onSearchProduto"
                >
                  <template slot="no-options">
                    Digite para buscar um produto
                  </template>
                  <template slot="option" slot-scope="option">
                    <div class="produto-option">
                      <strong>{{ option.descricao }}</strong>
                      <span>{{ produtoOptionResumo(option) }}</span>
                    </div>
                  </template>
                  <template slot="selected-option" slot-scope="option">
                    <div class="selected d-center">
                      {{ option.descricao }}
                    </div>
                  </template>
                </VueSelect>
              </b-form-group>
            </b-col>
            <b-col lg="3" md="6">
              <b-form-group label="Comanda" label-for="comanda_produto">
                <b-form-select
                  id="comanda_produto"
                  v-model="comandaAtivaId"
                  :options="comandaOptions"
                />
              </b-form-group>
            </b-col>
            <b-col lg="2" md="4">
              <b-form-group label="Quantidade" label-for="quantidade_produto">
                <b-form-input
                  id="quantidade_produto"
                  ref="quantidadeProdutoInput"
                  v-model.number="quantidadeProduto"
                  min="0.0001"
                  step="0.0001"
                  type="number"
                />
              </b-form-group>
            </b-col>
            <b-col v-if="canShowFinancialInfo" lg="2" md="4">
              <b-form-group label="Desconto" label-for="desconto_produto">
                <b-form-input
                  id="desconto_produto"
                  v-model.number="descontoProduto"
                  min="0"
                  step="0.01"
                  type="number"
                />
              </b-form-group>
            </b-col>
            <b-col lg="2" md="4">
              <button
                type="button"
                class="btn btn-primary btn-block font-weight-bold"
                :disabled="adicionandoProduto"
                @click="addProdutoAtendimento"
              >
                Adicionar produto
              </button>
            </b-col>
          </b-row>
          <b-form-group
            class="mb-0"
            label="Observação"
            label-for="observacao_produto"
          >
            <b-form-textarea
              id="observacao_produto"
              v-model="observacaoProduto"
              max-rows="4"
              placeholder="Ponto da carne, retirada de ingrediente, etc."
              rows="2"
            />
          </b-form-group>
        </div>

        <div class="itens-atendimento">
          <div class="itens-atendimento-header">
            <h5>Pedido atual</h5>
            <div class="itens-atendimento-header-actions">
              <span> {{ itensPedidoAtual.length }} item(ns) </span>
              <button
                v-if="itensHistoricoAtendimento.length"
                type="button"
                class="btn btn-sm btn-light-dark font-weight-bold"
                @click="$bvModal.show('modalHistoricoMesaRestaurante')"
              >
                Histórico da mesa
              </button>
            </div>
          </div>

          <div v-if="!itensPedidoAtual.length" class="empty-state py-5">
            Nenhum item em aberto para enviar.
          </div>

          <div v-else>
            <table
              class="table table-head-custom table-vertical-center table-sm mb-0 atendimento-table"
            >
              <thead>
                <tr>
                  <th>Produto</th>
                  <th>Comanda</th>
                  <th class="text-center">Qtde</th>
                  <th v-if="canShowFinancialInfo" class="text-right">
                    Unitário
                  </th>
                  <th v-if="canShowFinancialInfo" class="text-center">
                    Desconto
                  </th>
                  <th>Observação</th>
                  <th v-if="canShowFinancialInfo" class="text-right">Total</th>
                  <th class="text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="grupo in itensPedidoAtualPorComanda">
                  <tr
                    :key="`grupo-${grupo.comanda_id || 'sem'}`"
                    class="comanda-row"
                  >
                    <td :colspan="pedidoAtualColspan">
                      <strong>{{ grupo.label }}</strong>
                      <span>{{ grupoItensResumoLabel(grupo) }}</span>
                    </td>
                  </tr>
                  <tr
                    v-for="item in grupo.itens"
                    :key="item.id"
                    :class="{ 'item-cancelado': item.status === 'cancelado' }"
                  >
                    <td>
                      <strong>{{ produtoDescricao(item) }}</strong>
                      <span class="d-block text-muted font-size-sm">
                        #{{ item.produto_id }}
                      </span>
                    </td>
                    <td>{{ itemComandaLabel(item) }}</td>
                    <td>
                      <b-form-input
                        v-model.number="item.quantidade"
                        :disabled="!canEditItem(item)"
                        min="0.0001"
                        size="sm"
                        step="0.0001"
                        type="number"
                      />
                    </td>
                    <td v-if="canShowFinancialInfo" class="text-right">
                      {{ formatMoney(item.valor_unitario) }}
                    </td>
                    <td v-if="canShowFinancialInfo">
                      <b-form-input
                        v-model.number="item.desconto"
                        :disabled="!canEditItem(item)"
                        min="0"
                        size="sm"
                        step="0.01"
                        type="number"
                      />
                    </td>
                    <td>
                      <b-form-input
                        v-model="item.observacao"
                        :disabled="!canEditItem(item)"
                        size="sm"
                      />
                    </td>
                    <td v-if="canShowFinancialInfo" class="text-right">
                      <strong>{{ formatMoney(item.valor_total) }}</strong>
                    </td>
                    <td class="text-right">
                      <div class="atendimento-table-actions">
                        <button
                          v-if="canEditItem(item)"
                          type="button"
                          class="btn btn-icon btn-light-primary btn-sm"
                          title="Atualizar item"
                          :disabled="itemAtualizandoId === item.id"
                          @click="updateItemAtendimento(item)"
                        >
                          <i class="fas fa-save"></i>
                        </button>
                        <button
                          v-if="canCancelItem(item)"
                          type="button"
                          class="btn btn-icon btn-light-danger btn-sm"
                          title="Cancelar item"
                          :disabled="itemCancelandoId === item.id"
                          @click="cancelItemAtendimento(item)"
                        >
                          <i class="fas fa-times"></i>
                        </button>
                        <button
                          v-if="canTransferirEntreComandas(item)"
                          type="button"
                          class="btn btn-icon btn-light-dark btn-sm"
                          title="Transferir para outra comanda"
                          @click="openTransferComandaModal(item)"
                        >
                          <i class="fas fa-random"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>

        <div v-if="canShowFinancialInfo" class="totais-atendimento">
          <span>Total do atendimento</span>
          <strong>
            {{ formatMoney(mesaSelecionada.valor_total_atendimento || 0) }}
          </strong>
        </div>

        <div class="modal-actions">
          <button
            type="button"
            class="btn btn-light-dark font-weight-bold"
            @click="openTransferMesaModal"
          >
            Transferir mesa
          </button>
          <button
            type="button"
            class="btn btn-light-dark font-weight-bold"
            @click="openJuntarMesasModal"
          >
            Juntar mesas
          </button>
          <button
            v-if="transferableItens.length"
            type="button"
            class="btn btn-light-dark font-weight-bold"
            @click="openTransferItemModal"
          >
            Transferir item
          </button>
          <button
            v-if="transferableItens.length"
            type="button"
            class="btn btn-light-dark font-weight-bold"
            @click="openSplitItensModal"
          >
            Separar itens
          </button>
          <button
            v-if="canSendItensProducao"
            type="button"
            class="btn btn-warning font-weight-bold"
            :disabled="enviandoProducao"
            @click="sendItensProducao"
          >
            Enviar para cozinha/bar
          </button>
          <button
            v-if="canCancelAtendimentoSelecionado"
            type="button"
            class="btn btn-light-danger font-weight-bold"
            @click="cancelAtendimentoVazio"
          >
            Cancelar atendimento vazio
          </button>
          <button
            v-if="canCheckoutOperacional"
            type="button"
            class="btn btn-success font-weight-bold"
            @click="openCheckout"
          >
            Fechar conta
          </button>
          <button
            type="button"
            class="btn btn-clean font-weight-bold"
            @click="$bvModal.hide('modalAtendimentoRestaurante')"
          >
            Fechar
          </button>
        </div>
      </div>
    </b-modal>

    <b-modal
      id="modalJuntarMesasRestaurante"
      title="Juntar mesas"
      centered
      hide-footer
    >
      <div v-if="mesaSelecionada">
        <b-form-group label="Juntar com a mesa" label-for="mesa_destino_juntar">
          <b-form-select
            id="mesa_destino_juntar"
            v-model="juntarMesasForm.mesa_destino_id"
            :options="mesaJuncaoOptions"
          />
        </b-form-group>
        <div class="operation-summary">
          <strong>Mesa origem:</strong>
          {{ mesaDisplayLabel(mesaSelecionada) }}
          <br />
          <strong>Mesa mãe:</strong>
          {{ selectedMesaDestinoLabel(juntarMesasForm.mesa_destino_id) }}
          <br />
          As comandas da mesa origem serão preservadas e movidas para o bloco da
          mesa mãe.
        </div>
        <div class="modal-actions">
          <button
            type="button"
            class="btn btn-clean font-weight-bold"
            @click="$bvModal.hide('modalJuntarMesasRestaurante')"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="btn btn-primary font-weight-bold"
            :disabled="operacaoAtendimentoEmAndamento"
            @click="confirmJuntarMesas"
          >
            Juntar mesas
          </button>
        </div>
      </div>
    </b-modal>

    <b-modal
      id="modalComandaAvulsaRestaurante"
      hide-footer
      no-enforce-focus
      size="xl"
      :title="comandaAvulsaModalTitle"
      @shown="focusComandaAvulsaCodigo"
    >
      <div class="comanda-avulsa-modal">
        <b-row class="align-items-end">
          <b-col lg="4" md="6">
            <b-form-group label="Código da comanda" label-for="codigo_avulsa">
              <b-form-input
                id="codigo_avulsa"
                ref="codigoComandaAvulsa"
                v-model.trim="comandaAvulsaForm.codigo"
                autocomplete="off"
                placeholder="Digite ou leia o código"
                @keyup.enter="buscarComandaAvulsa"
              />
            </b-form-group>
          </b-col>
          <b-col v-if="modoApenasComandas" lg="4" md="6">
            <b-form-group label="Nome da comanda" label-for="nome_avulsa">
              <b-form-input
                id="nome_avulsa"
                v-model.trim="comandaAvulsaForm.nome"
                autocomplete="off"
                placeholder="Nome do cliente ou identificação"
                @keyup.enter="buscarComandaAvulsa"
              />
            </b-form-group>
          </b-col>
          <b-col v-if="!modoApenasComandas" lg="4" md="6">
            <b-form-group label="Vincular à mesa" label-for="mesa_avulsa">
              <b-form-select
                id="mesa_avulsa"
                v-model="comandaAvulsaForm.mesa_id"
                :options="mesaVinculoAvulsaOptions"
              />
            </b-form-group>
          </b-col>
          <b-col lg="4" md="12">
            <div class="avulsa-actions">
              <button
                type="button"
                class="btn btn-light-dark font-weight-bold"
                :disabled="comandaAvulsaLoading"
                @click="buscarComandaAvulsa"
              >
                {{ modoApenasComandas ? "Criar/abrir" : "Buscar/criar" }}
              </button>
              <button
                v-if="!modoApenasComandas"
                type="button"
                class="btn btn-light-primary font-weight-bold"
                :disabled="
                  !comandaAvulsaForm.codigo ||
                  !comandaAvulsaForm.mesa_id ||
                  comandaAvulsaLoading
                "
                @click="vincularComandaAvulsaMesa"
              >
                Vincular mesa
              </button>
              <button
                v-if="canCheckoutOperacional"
                type="button"
                class="btn btn-success font-weight-bold"
                :disabled="!comandaAvulsaData"
                @click="openCheckoutComandaAvulsa"
              >
                Cobrar
              </button>
              <button
                v-if="modoApenasComandas"
                type="button"
                class="btn btn-light-danger font-weight-bold"
                :disabled="
                  !comandaAvulsaComanda ||
                  comandaAvulsaItens.length > 0 ||
                  comandaAvulsaLoading
                "
                @click="cancelarComandaModoVazia"
              >
                Cancelar vazia
              </button>
            </div>
          </b-col>
        </b-row>

        <div v-if="!modoApenasComandas" class="avulsas-sem-mesa-compact">
          <b-row class="align-items-end">
            <b-col lg="8" md="7">
              <b-form-group
                label="Avulsas sem mesa"
                label-for="avulsas_sem_mesa"
              >
                <b-form-select
                  id="avulsas_sem_mesa"
                  v-model="comandaAvulsaSelecionadaId"
                  :disabled="comandasAvulsasSemMesaLoading"
                  :options="comandasAvulsasSemMesaOptions"
                  @change="selectComandaAvulsaSemMesaById"
                />
              </b-form-group>
            </b-col>
            <b-col lg="4" md="5">
              <button
                type="button"
                class="btn btn-light-dark btn-block font-weight-bold"
                :disabled="comandasAvulsasSemMesaLoading"
                @click="getComandasAvulsasSemMesa"
              >
                {{
                  comandasAvulsasSemMesaLoading
                    ? "Carregando..."
                    : "Atualizar lista"
                }}
              </button>
            </b-col>
          </b-row>
        </div>

        <div v-if="comandaAvulsaData" class="avulsa-status">
          <strong>{{ comandaAvulsaTitulo }}</strong>
          <span>{{ comandaAvulsaMesaLabel }}</span>
          <span>{{ comandaAvulsaItens.length }} item(ns)</span>
          <span v-if="canShowFinancialInfo">
            {{ formatMoney(comandaAvulsaTotal) }}
          </span>
        </div>

        <b-row>
          <b-col lg="5">
            <div class="produto-atendimento-form h-100">
              <h5 class="mb-4">Lançar item</h5>
              <SeletorProdutosGarcom
                ref="seletorProdutosGarcomAvulsa"
                :quantidades="quantidadesAdicionadasAvulsa"
                @quick-add="onQuickAddProdutoAvulsa"
                @select="onSelectProdutoAvulsa"
              />
              <b-form-group label="Produto" label-for="produto_avulsa">
                <VueSelect
                  id="produto_avulsa"
                  v-model="comandaAvulsaForm.produto"
                  :filterable="false"
                  :options="produtosAvulsa"
                  label="descricao"
                  placeholder="Buscar produto"
                  @search="onSearchProdutoAvulsa"
                >
                  <template slot="no-options">
                    Digite para buscar um produto
                  </template>
                  <template slot="option" slot-scope="option">
                    <div class="produto-option">
                      <strong>{{ option.descricao }}</strong>
                      <span>{{ produtoOptionResumo(option) }}</span>
                    </div>
                  </template>
                  <template slot="selected-option" slot-scope="option">
                    <div class="selected d-center">
                      {{ option.descricao }}
                    </div>
                  </template>
                </VueSelect>
              </b-form-group>
              <b-row>
                <b-col md="6">
                  <b-form-group label="Quantidade" label-for="qtd_avulsa">
                    <b-form-input
                      id="qtd_avulsa"
                      ref="quantidadeProdutoInputAvulsa"
                      v-model.number="comandaAvulsaForm.quantidade"
                      min="0.0001"
                      step="0.0001"
                      type="number"
                    />
                  </b-form-group>
                </b-col>
                <b-col v-if="canShowFinancialInfo" md="6">
                  <b-form-group label="Desconto" label-for="desconto_avulsa">
                    <b-form-input
                      id="desconto_avulsa"
                      v-model.number="comandaAvulsaForm.desconto"
                      min="0"
                      step="0.01"
                      type="number"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
              <b-form-group label="Observação" label-for="obs_avulsa">
                <b-form-textarea
                  id="obs_avulsa"
                  v-model="comandaAvulsaForm.observacao"
                  max-rows="4"
                  rows="2"
                />
              </b-form-group>
              <button
                type="button"
                class="btn btn-primary btn-block font-weight-bold"
                :disabled="comandaAvulsaLoading"
                @click="addProdutoComandaAvulsa"
              >
                {{ modoApenasComandas ? "Adicionar na comanda" : "Adicionar na avulsa" }}
              </button>
            </div>
          </b-col>
          <b-col lg="7">
            <div class="itens-atendimento h-100">
              <div class="itens-atendimento-header">
                <h5>Itens da comanda</h5>
                <button
                  v-if="comandaAvulsaItensPendentes.length"
                  type="button"
                  class="btn btn-sm btn-warning font-weight-bold"
                  :disabled="comandaAvulsaLoading"
                  @click="sendProducaoComandaAvulsa"
                >
                  Enviar cozinha/bar
                </button>
              </div>
              <div v-if="!comandaAvulsaItens.length" class="empty-state py-5">
                Nenhum item lançado nesta comanda.
              </div>
              <table v-else class="table table-sm table-vertical-center mb-0">
                <thead>
                  <tr>
                    <th>Produto</th>
                    <th class="text-center">Qtde</th>
                    <th v-if="canShowFinancialInfo" class="text-right">
                      Total
                    </th>
                    <th>Status</th>
                    <th class="text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in comandaAvulsaItens" :key="item.id">
                    <td>
                      <strong>{{ produtoDescricao(item) }}</strong>
                      <span
                        v-if="item.observacao"
                        class="d-block text-muted font-size-sm"
                      >
                        {{ item.observacao }}
                      </span>
                    </td>
                    <td class="text-center">{{ item.quantidade }}</td>
                    <td v-if="canShowFinancialInfo" class="text-right">
                      <strong>{{ formatMoney(item.valor_total) }}</strong>
                    </td>
                    <td>{{ statusItemLabel(item.status) }}</td>
                    <td class="text-right">
                      <button
                        v-if="canCancelItem(item)"
                        type="button"
                        class="btn btn-icon btn-light-danger btn-sm"
                        title="Cancelar item"
                        @click="cancelItemComandaAvulsa(item)"
                      >
                        <i class="fas fa-times"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </b-col>
        </b-row>
      </div>
    </b-modal>

    <b-modal
      id="modalHistoricoMesaRestaurante"
      title="Histórico da mesa"
      centered
      hide-footer
      no-enforce-focus
      size="xl"
    >
      <div v-if="mesaSelecionada">
        <div class="historico-mesa-summary">
          <div>
            <span>Mesa</span>
            <strong>{{ mesaSelecionada.numero }}</strong>
          </div>
          <div>
            <span>Atendimento</span>
            <strong>#{{ mesaSelecionada.atendimento_aberto_id }}</strong>
          </div>
          <div>
            <span>Histórico</span>
            <strong>{{ itensHistoricoAtendimento.length }} item(ns)</strong>
          </div>
        </div>

        <div v-if="!itensHistoricoAtendimento.length" class="empty-state py-5">
          Nenhum item enviado para produção ou entregue.
        </div>

        <div v-else class="table-responsive">
          <table
            class="table table-head-custom table-vertical-center table-sm mb-0 atendimento-table atendimento-historico-table"
          >
            <thead>
              <tr>
                <th>Produto</th>
                <th>Comanda</th>
                <th class="text-center">Qtde</th>
                <th v-if="canShowFinancialInfo" class="text-right">Unitário</th>
                <th v-if="canShowFinancialInfo" class="text-right">Total</th>
                <th>Status</th>
                <th>Observação</th>
                <th class="text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="grupo in itensHistoricoPorComanda">
                <tr
                  :key="`historico-grupo-${grupo.comanda_id || 'sem'}`"
                  class="comanda-row"
                >
                  <td :colspan="historicoColspan">
                    <strong>{{ grupo.label }}</strong>
                    <span>{{ grupoItensResumoLabel(grupo) }}</span>
                  </td>
                </tr>
                <tr
                  v-for="item in grupo.itens"
                  :key="`historico-${item.id}`"
                  :class="{ 'item-cancelado': item.status === 'cancelado' }"
                >
                  <td>
                    <strong>{{ produtoDescricao(item) }}</strong>
                    <span class="d-block text-muted font-size-sm">
                      #{{ item.produto_id }}
                    </span>
                  </td>
                  <td>{{ itemComandaLabel(item) }}</td>
                  <td class="text-center">
                    {{ formatQuantity(item.quantidade) }}
                  </td>
                  <td v-if="canShowFinancialInfo" class="text-right">
                    {{ formatMoney(item.valor_unitario) }}
                  </td>
                  <td v-if="canShowFinancialInfo" class="text-right">
                    <strong>{{ formatMoney(item.valor_total) }}</strong>
                  </td>
                  <td>
                    <span :class="statusItemClass(item.status)">
                      {{ statusItemLabel(item.status) }}
                    </span>
                  </td>
                  <td>{{ item.observacao || "-" }}</td>
                  <td class="text-right">
                    <button
                      v-if="canCancelProductionItem(item)"
                      type="button"
                      class="btn btn-icon btn-light-danger btn-sm"
                      title="Cancelar item"
                      @click="cancelItemAtendimento(item)"
                    >
                      <i class="fas fa-times"></i>
                    </button>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>

        <div class="modal-actions">
          <button
            type="button"
            class="btn btn-clean font-weight-bold"
            @click="$bvModal.hide('modalHistoricoMesaRestaurante')"
          >
            Fechar
          </button>
        </div>
      </div>
    </b-modal>

    <b-modal
      id="modalNovaComandaRestaurante"
      title="Nova comanda"
      centered
      hide-footer
    >
      <div v-if="mesaSelecionada">
        <b-form-group label="Nome" label-for="nova_comanda_nome">
          <b-form-input
            id="nova_comanda_nome"
            v-model="novaComandaForm.nome"
            placeholder="Ex: João, Cliente 2"
          />
        </b-form-group>
        <b-form-group label="Observação" label-for="nova_comanda_observacao">
          <b-form-textarea
            id="nova_comanda_observacao"
            v-model="novaComandaForm.observacao"
            rows="3"
          />
        </b-form-group>
        <div class="modal-actions">
          <button
            type="button"
            class="btn btn-clean font-weight-bold"
            @click="$bvModal.hide('modalNovaComandaRestaurante')"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="btn btn-primary font-weight-bold"
            :disabled="operacaoAtendimentoEmAndamento"
            @click="confirmNovaComanda"
          >
            Criar comanda
          </button>
        </div>
      </div>
    </b-modal>

    <b-modal
      id="modalPrimeiraComandaRestaurante"
      title="Primeira comanda"
      centered
      hide-footer
      no-close-on-backdrop
      no-close-on-esc
      hide-header-close
    >
      <div v-if="primeiraComandaParaNomear">
        <p class="modal-atendimento-text">
          Informe o nome da primeira comanda deste atendimento.
        </p>
        <b-form-group label="Nome" label-for="primeira_comanda_nome">
          <b-form-input
            id="primeira_comanda_nome"
            ref="primeiraComandaNome"
            v-model="primeiraComandaForm.nome"
            placeholder="Ex: Mateus, Cliente 1"
            @keyup.enter="confirmPrimeiraComanda"
          />
        </b-form-group>
        <div class="modal-actions">
          <button
            type="button"
            class="btn btn-primary font-weight-bold"
            :disabled="operacaoAtendimentoEmAndamento"
            @click="confirmPrimeiraComanda"
          >
            Confirmar comanda
          </button>
        </div>
      </div>
    </b-modal>

    <b-modal
      id="modalTransferirComandaRestaurante"
      title="Transferir item entre comandas"
      centered
      hide-footer
    >
      <div v-if="transferComandaForm.item">
        <p class="modal-atendimento-text">
          <strong>{{ produtoDescricao(transferComandaForm.item) }}</strong>
          <br />
          {{ itemComandaLabel(transferComandaForm.item) }}
        </p>
        <b-form-group label="Comanda destino" label-for="comanda_destino">
          <b-form-select
            id="comanda_destino"
            v-model="transferComandaForm.comanda_destino_id"
            :options="transferComandaDestinoOptions"
          />
        </b-form-group>
        <div class="modal-actions">
          <button
            type="button"
            class="btn btn-clean font-weight-bold"
            @click="$bvModal.hide('modalTransferirComandaRestaurante')"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="btn btn-primary font-weight-bold"
            :disabled="operacaoAtendimentoEmAndamento"
            @click="confirmTransferComanda"
          >
            Transferir item
          </button>
        </div>
      </div>
    </b-modal>

    <b-modal
      id="modalTransferirMesaRestaurante"
      title="Transferir mesa"
      centered
      hide-footer
    >
      <div v-if="mesaSelecionada">
        <b-form-group label="Mesa destino" label-for="mesa_destino_transferir">
          <b-form-select
            id="mesa_destino_transferir"
            v-model="transferMesaForm.mesa_destino_id"
            :options="mesaDestinoOptions"
          />
        </b-form-group>
        <b-form-checkbox v-model="transferMesaForm.juntar">
          Juntar com atendimento aberto se a mesa destino estiver ocupada
        </b-form-checkbox>
        <div class="operation-summary">
          <strong>Mesa origem:</strong>
          Mesa {{ mesaSelecionada.numero }}
          <br />
          <strong>Mesa destino:</strong>
          {{ selectedMesaDestinoLabel(transferMesaForm.mesa_destino_id) }}
        </div>
        <div class="modal-actions">
          <button
            type="button"
            class="btn btn-clean font-weight-bold"
            @click="$bvModal.hide('modalTransferirMesaRestaurante')"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="btn btn-primary font-weight-bold"
            :disabled="operacaoAtendimentoEmAndamento"
            @click="confirmTransferMesa"
          >
            Confirmar transferência
          </button>
        </div>
      </div>
    </b-modal>

    <b-modal
      id="modalTransferirItemRestaurante"
      title="Transferir item"
      centered
      hide-footer
    >
      <div v-if="mesaSelecionada">
        <b-form-group label="Item" label-for="item_transferir">
          <b-form-select
            id="item_transferir"
            v-model="transferItemForm.item_id"
            :options="itemTransferOptions"
          />
        </b-form-group>
        <b-form-group
          label="Atendimento destino"
          label-for="atendimento_destino"
        >
          <b-form-select
            id="atendimento_destino"
            v-model="transferItemForm.atendimento_destino_id"
            :options="atendimentoDestinoOptions"
          />
        </b-form-group>
        <div class="operation-summary">
          O item selecionado sairá da Mesa {{ mesaSelecionada.numero }} e será
          lançado no atendimento destino.
        </div>
        <div class="modal-actions">
          <button
            type="button"
            class="btn btn-clean font-weight-bold"
            @click="$bvModal.hide('modalTransferirItemRestaurante')"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="btn btn-primary font-weight-bold"
            :disabled="operacaoAtendimentoEmAndamento"
            @click="confirmTransferItem"
          >
            Confirmar transferência
          </button>
        </div>
      </div>
    </b-modal>

    <b-modal
      id="modalSepararItensRestaurante"
      title="Separar itens"
      centered
      hide-footer
    >
      <div v-if="mesaSelecionada">
        <b-form-group label="Itens" label-for="itens_separar">
          <b-form-checkbox-group
            id="itens_separar"
            v-model="splitItensForm.item_ids"
            :options="itemCheckboxOptions"
            stacked
          />
        </b-form-group>
        <b-form-group label="Nova mesa" label-for="mesa_destino_separar">
          <b-form-select
            id="mesa_destino_separar"
            v-model="splitItensForm.mesa_destino_id"
            :options="mesaLivreOptions"
          />
        </b-form-group>
        <div class="operation-summary">
          Será criado um novo atendimento na mesa livre selecionada com os itens
          marcados.
        </div>
        <div class="modal-actions">
          <button
            type="button"
            class="btn btn-clean font-weight-bold"
            @click="$bvModal.hide('modalSepararItensRestaurante')"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="btn btn-primary font-weight-bold"
            :disabled="operacaoAtendimentoEmAndamento"
            @click="confirmSplitItens"
          >
            Separar itens
          </button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import _ from "lodash";
import { PessoaMixin, SistemaMixin } from "@/modules/Application";
import VueSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import { restauranteFeatureFlags } from "../../config";
import { imprimirTicketsProducao } from "../../services/qzTrayPrinter";
import SeletorProdutosGarcom from "../../components/SeletorProdutosGarcom.vue";

export default {
  name: "app-restaurante-mesas-operacional",
  components: {
    VueSelect,
    SeletorProdutosGarcom,
  },
  mixins: [PessoaMixin, SistemaMixin],
  props: {
    perfilOperacional: {
      type: String,
      default: "caixa",
    },
    produtoDestino: {
      type: String,
      default: null,
    },
    titulo: {
      type: String,
      default: "Salão Restaurante",
    },
    subtitulo: {
      type: String,
      default: "Mesa, comanda, produção e fechamento em uma operação só",
    },
    exibirCheckout: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      ambienteId: null,
      ambientes: [],
      buscaMesaSalao: "",
      ambienteOptions: [{ value: null, text: "Todos" }],
      reservas: [],
      reservasLoading: false,
      reservaForm: {
        nome: "",
        data: "",
        horario: "",
        horario_fim: "",
        mesa_ids: [],
        quantidade_pessoas: null,
      },
      visualizacaoSalao: "mapa",
      editandoLayout: false,
      layoutDrafts: {},
      mesaLayoutSelecionadaId: null,
      layoutDrag: null,
      featureFlags: restauranteFeatureFlags,
      restauranteUsarApenasComandas: false,
      // Visao ativa no salao: 'mesas' ou 'comandas'. Escolha do usuario com
      // persistencia por empresa/device; default = modo da empresa.
      visualizacaoOperacao: null,
      restauranteCardapioOnlineAtivo: false,
      comandasModo: [],
      comandasModoMeta: null,
      comandasModoLoading: false,
      comandasModoSearch: "",
      comandasModoFiltro: "todas",
      mesaSelecionada: null,
      atendimentoSelecionado: null,
      itensAtendimento: [],
      comandas: [],
      comandaAtivaId: null,
      mesaParaAbrir: null,
      reservaParaAbrir: null,
      reservasParaUtilizar: [],
      mesaParaAbrirReserva: null,
      reservaUtilizarSelecionada: null,
      clienteSelecionado: null,
      clientes: [],
      aberturaAtendimentoForm: {
        tipo: "nome",
        nome: "",
        codigo: "",
      },
      aberturaAtendimentoTipos: [
        { value: "nome", text: "Informar nome" },
        { value: "codigo", text: "Informar comanda" },
      ],
      produtoSelecionado: null,
      produtos: [],
      produtosAvulsa: [],
      quantidadeProduto: 1,
      descontoProduto: 0,
      observacaoProduto: "",
      quantidadesAdicionadasSessao: {},
      // Badge de contagem por produto no seletor da comanda avulsa
      // (mesmo formato { [produtoId]: numero } do atendimento de mesa).
      quantidadesAdicionadasAvulsa: {},
      comandaAvulsaLoading: false,
      comandaAvulsaData: null,
      comandaAvulsaSelecionadaId: null,
      comandasAvulsasSemMesaLoading: false,
      comandasAvulsasSemMesa: [],
      comandaAvulsaForm: {
        codigo: "",
        nome: "",
        mesa_id: null,
        produto: null,
        quantidade: 1,
        desconto: 0,
        observacao: "",
      },
      abrindoAtendimento: false,
      juntandoMesaAbertura: false,
      adicionandoProduto: false,
      enviandoProducao: false,
      entregasProntas: [],
      expandedEntregaTickets: {},
      entregandoProducaoId: null,
      itemAtualizandoId: null,
      itemCancelandoId: null,
      entregasRefreshInterval: null,
      operacaoAtendimentoEmAndamento: false,
      transferMesaForm: {
        mesa_destino_id: null,
        juntar: false,
      },
      juntarMesasForm: {
        mesa_destino_id: null,
      },
      juntarMesaAberturaForm: {
        mesa_destino_id: null,
      },
      transferItemForm: {
        item_id: null,
        atendimento_destino_id: null,
      },
      splitItensForm: {
        mesa_destino_id: null,
        item_ids: [],
      },
      novaComandaForm: {
        nome: "",
        observacao: "",
      },
      primeiraComandaParaNomear: null,
      primeiraComandaForm: {
        nome: "",
      },
      transferComandaForm: {
        item: null,
        comanda_destino_id: null,
      },
      statusList: [
        { value: "livre", text: "Livre" },
        { value: "ocupada", text: "Ocupada" },
        { value: "reservada", text: "Reservada" },
        { value: "inativa", text: "Inativa" },
      ],
    };
  },
  computed: {
    ...mapGetters(["itsblocked"]),
    reservaAtualMesaParaAbrir() {
      return this.reservaParaAbrir || this.mesaParaAbrir?.reserva_atual || null;
    },
    modoApenasComandas() {
      // Agora reflete a VISAO ATIVA escolhida pelo usuario no salao, nao mais
      // apenas a config da empresa. O default vem do modo da empresa.
      return this.visualizacaoOperacao === "comandas";
    },
    visualizacaoOperacaoLabel() {
      return this.modoApenasComandas ? "Comandas" : "Mesas";
    },
    salaoStats() {
      return this.allMesas.reduce(
        (acc, mesa) => {
          acc.total += 1;

          if (mesa.status === "ocupada") {
            acc.ocupadas += 1;
          }

          if (mesa.status === "livre") {
            acc.livres += 1;
          }

          if (mesa.status === "reservada") {
            acc.reservadas += 1;
          }

          if (!mesa.mesa_mae_id) {
            acc.valorAberto += Number(mesa.valor_total_atendimento || 0);
            acc.valorPendente += this.mesaValorPendente(mesa);
            acc.itens += Number(mesa.quantidade_itens || 0);
            acc.producaoPendente += Number(
              mesa.producao_pendente ||
                mesa.itens_producao_pendente ||
                mesa.quantidade_producao_pendente ||
                0
            );
            acc.producaoPronta += Number(
              mesa.producao_pronta ||
                mesa.itens_prontos ||
                mesa.quantidade_producao_pronta ||
                0
            );
          }

          return acc;
        },
        {
          total: 0,
          ocupadas: 0,
          livres: 0,
          reservadas: 0,
          valorAberto: 0,
          valorPendente: 0,
          itens: 0,
          producaoPendente: 0,
          producaoPronta: 0,
        }
      );
    },
    salaoMetrics() {
      const stats = this.salaoStats;

      const metrics = [
        {
          label: "Mesas ocupadas",
          value: `${stats.ocupadas}/${stats.total}`,
          hint: `${stats.livres} livres, ${stats.reservadas} reservadas`,
        },
      ];

      if (this.canShowFinancialInfo) {
        metrics.push(
          {
            label: "Contas abertas",
            value: this.formatMoney(stats.valorAberto),
            hint: `${stats.itens} item(ns) lançados`,
          },
          {
            label: "Pendente",
            value: this.formatMoney(stats.valorPendente || stats.valorAberto),
            hint: "saldo estimado para fechamento",
          }
        );
      }

      metrics.push({
        label: "Produção",
        value: `${stats.producaoPendente} / ${stats.producaoPronta}`,
        hint: "pendentes / prontos",
      });

      return metrics;
    },
    comandasModoStats() {
      return this.comandasModo.reduce(
        (acc, comanda) => {
          const itens = Number(comanda.quantidade_itens_ativos || 0);
          const enviados = Number(comanda.quantidade_itens_enviados || 0);
          const prontos = Number(comanda.quantidade_itens_prontos || 0);

          acc.total += 1;
          acc.itens += itens;
          acc.enviadas += enviados > 0 ? 1 : 0;
          acc.prontas += prontos > 0 ? 1 : 0;
          acc.valorAberto += Number(comanda.valor_total || 0);

          if (itens === 0) {
            acc.semItens += 1;
          }

          return acc;
        },
        {
          total: 0,
          semItens: 0,
          itens: 0,
          enviadas: 0,
          prontas: 0,
          valorAberto: 0,
        }
      );
    },
    comandasModoMetrics() {
      const stats = this.comandasModoStats;
      const metrics = [
        {
          label: "Comandas abertas",
          value: stats.total,
          hint: `${stats.semItens} sem itens`,
        },
        {
          label: "Itens",
          value: stats.itens,
          hint: `${stats.enviadas} enviada(s), ${stats.prontas} pronta(s)`,
        },
      ];

      if (this.canShowFinancialInfo) {
        metrics.push({
          label: "Saldo aberto",
          value: this.formatMoney(stats.valorAberto),
          hint: "total das comandas abertas",
        });
      }

      return metrics;
    },
    operacaoMetrics() {
      return this.modoApenasComandas
        ? this.comandasModoMetrics
        : this.salaoMetrics;
    },
    comandasModoFiltros() {
      return [
        { value: "todas", text: "Todas" },
        { value: "com_itens", text: "Com itens" },
        { value: "sem_itens", text: "Sem itens" },
        { value: "enviadas", text: "Produção" },
        { value: "prontas", text: "Prontas" },
        { value: "com_saldo", text: "Com saldo" },
      ];
    },
    canCancelAtendimentoSelecionado() {
      if (
        !this.mesaSelecionada ||
        !this.mesaSelecionada.atendimento_aberto_id
      ) {
        return false;
      }

      const quantidadeItens = Number(
        this.mesaSelecionada.quantidade_itens || 0
      );
      const valorTotal = Number(
        this.mesaSelecionada.valor_total_atendimento || 0
      );

      return quantidadeItens === 0 && valorTotal === 0;
    },
    itensPedidoAtual() {
      return this.itensAtendimento.filter((item) =>
        this.isPedidoAtualItem(item)
      );
    },
    itensHistoricoAtendimento() {
      return this.itensAtendimento.filter(
        (item) => !this.isPedidoAtualItem(item)
      );
    },
    canSendItensProducao() {
      return this.itensPedidoAtual.length > 0;
    },
    canCheckoutOperacional() {
      return this.exibirCheckout && this.perfilOperacional === "caixa";
    },
    canShowFinancialInfo() {
      return this.perfilOperacional === "caixa";
    },
    canShowGestaoShortcuts() {
      return (
        !this.modoApenasComandas &&
        !["garcom", "bar", "caixa"].includes(this.perfilOperacional)
      );
    },
    canShowQrMesa() {
      return (
        !this.modoApenasComandas &&
        !["garcom", "bar", "caixa"].includes(this.perfilOperacional)
      );
    },
    canShowComandaAvulsa() {
      return ["garcom", "caixa"].includes(this.perfilOperacional);
    },
    canShowPedidosOnlineShortcut() {
      return (
        this.perfilOperacional === "caixa" &&
        this.restauranteCardapioOnlineAtivo
      );
    },
    comandaAvulsaActionLabel() {
      return this.modoApenasComandas ? "Nova comanda" : "Comandas avulsas";
    },
    comandaAvulsaModalTitle() {
      return this.modoApenasComandas ? "Comanda" : "Comandas avulsas";
    },
    canShowEntregaQueue() {
      return this.perfilOperacional === "garcom";
    },
    comandaAvulsaComanda() {
      return this.comandaAvulsaData?.comanda || null;
    },
    comandaAvulsaItens() {
      return this.comandaAvulsaData?.itens || [];
    },
    comandaAvulsaItensPendentes() {
      return this.comandaAvulsaItens.filter((item) =>
        this.isPedidoAtualItem(item)
      );
    },
    comandaAvulsaTotal() {
      return this.comandaAvulsaItens.reduce(
        (total, item) =>
          item.status === "cancelado"
            ? total
            : total + Number(item.valor_total || 0),
        0
      );
    },
    comandaAvulsaTitulo() {
      if (this.modoApenasComandas) {
        return this.comandaModoTitulo(this.comandaAvulsaComanda);
      }

      const codigo =
        this.comandaAvulsaComanda?.codigo || this.comandaAvulsaForm.codigo;

      return codigo ? `Avulsa ${codigo}` : "Comanda avulsa";
    },
    comandaAvulsaMesaLabel() {
      if (this.modoApenasComandas) {
        return "Modo apenas comandas";
      }

      const mesa =
        this.comandaAvulsaData?.mesa || this.comandaAvulsaComanda?.mesa;

      if (!mesa) {
        return "Sem mesa vinculada";
      }

      return `Mesa ${mesa.numero || mesa.id}`;
    },
    pedidoAtualColspan() {
      return this.canShowFinancialInfo ? 8 : 5;
    },
    historicoColspan() {
      return this.canShowFinancialInfo ? 8 : 6;
    },
    mesaVinculoAvulsaOptions() {
      return [
        { value: null, text: "Sem mesa" },
        ...this.allMesas
          .filter((mesa) => mesa.ativo && mesa.status !== "inativa")
          .map((mesa) => ({
            value: mesa.id,
            text: `Mesa ${mesa.numero} - ${mesa.ambiente_nome}`,
          })),
      ];
    },
    reservaMesasMapa() {
      return this.allMesas.filter(
        (mesa) => mesa.ativo && mesa.status !== "inativa"
      );
    },
    reservaCapacidadeSelecionada() {
      return this.reservaMesasMapa.reduce((total, mesa) => {
        if (!this.reservaForm.mesa_ids.includes(mesa.id)) {
          return total;
        }

        return total + Number(mesa.capacidade || 0);
      }, 0);
    },
    reservaMesasSelecionadasResumo() {
      if (!this.reservaForm.mesa_ids.length) {
        return "Nenhuma mesa selecionada";
      }

      const numeros = this.reservaMesasMapa
        .filter((mesa) => this.reservaForm.mesa_ids.includes(mesa.id))
        .map((mesa) => mesa.numero);

      return `Mesas ${numeros.join(", ")}`;
    },
    comandasAvulsasSemMesaOptions() {
      return [
        {
          value: null,
          text: this.comandasAvulsasSemMesa.length
            ? "Selecionar comanda avulsa"
            : "Nenhuma comanda avulsa sem mesa",
        },
        ...this.comandasAvulsasSemMesa.map((avulsa) => ({
          value: avulsa.comanda.id,
          text: this.canShowFinancialInfo
            ? `${this.comandaAvulsaListLabel(
                avulsa
              )} - ${this.comandaAvulsaListResumo(
                avulsa
              )} - ${this.comandaAvulsaListTotal(avulsa)}`
            : `${this.comandaAvulsaListLabel(
                avulsa
              )} - ${this.comandaAvulsaListResumo(avulsa)}`,
        })),
      ];
    },
    entregaTickets() {
      const tickets = this.entregasProntas.reduce((acc, item) => {
        const key = this.entregaTicketKey(item);

        if (!acc[key]) {
          acc[key] = {
            key,
            firstItem: item,
            items: [],
          };
        }

        acc[key].items.push(item);

        if (
          this.entregaItemTimestamp(item) <
          this.entregaItemTimestamp(acc[key].firstItem)
        ) {
          acc[key].firstItem = item;
        }

        return acc;
      }, {});

      return Object.values(tickets)
        .map((ticket) => ({
          ...ticket,
          items: ticket.items.sort(
            (a, b) =>
              this.entregaItemTimestamp(a) - this.entregaItemTimestamp(b)
          ),
        }))
        .sort(
          (a, b) =>
            this.entregaItemTimestamp(a.firstItem) -
            this.entregaItemTimestamp(b.firstItem)
        );
    },
    comandaOptions() {
      return [
        { value: null, text: "Selecione" },
        ...this.comandas
          .filter((comanda) => comanda.status === "aberta")
          .map((comanda) => ({
            value: comanda.id,
            text: this.comandaResumoLabel(comanda),
          })),
      ];
    },
    itensPedidoAtualPorComanda() {
      return this.groupItensByComanda(this.itensPedidoAtual);
    },
    itensHistoricoPorComanda() {
      return this.groupItensByComanda(this.itensHistoricoAtendimento);
    },
    transferComandaDestinoOptions() {
      const item = this.transferComandaForm.item;

      return [
        { value: null, text: "Selecione" },
        ...this.comandas
          .filter(
            (comanda) =>
              comanda.status === "aberta" && comanda.id !== item?.comanda_id
          )
          .map((comanda) => ({
            value: comanda.id,
            text: this.comandaLabel(comanda),
          })),
      ];
    },
    allMesas() {
      return this.sortMesas(
        this.ambientes.flatMap((ambiente) =>
          ambiente.mesas.map((mesa) => ({
            ...mesa,
            ambiente_nome: ambiente.nome,
          }))
        )
      );
    },
    mesaDestinoOptions() {
      return [
        { value: null, text: "Selecione" },
        ...this.allMesas
          .filter(
            (mesa) =>
              this.mesaSelecionada &&
              mesa.id !== this.mesaSelecionada.id &&
              mesa.status !== "inativa"
          )
          .map((mesa) => ({
            value: mesa.id,
            text: `Mesa ${mesa.numero} - ${
              mesa.ambiente_nome
            } (${this.statusLabel(mesa.status)})`,
          })),
      ];
    },
    mesaJuncaoOptions() {
      return this.mesaJuncaoOptionsFor(this.mesaSelecionada);
    },
    mesaJuncaoAberturaOptions() {
      return this.mesaJuncaoOptionsFor(this.mesaParaAbrir, true);
    },
    mesaLivreOptions() {
      return [
        { value: null, text: "Selecione" },
        ...this.allMesas
          .filter(
            (mesa) =>
              this.mesaSelecionada &&
              mesa.id !== this.mesaSelecionada.id &&
              mesa.status === "livre"
          )
          .map((mesa) => ({
            value: mesa.id,
            text: `Mesa ${mesa.numero} - ${mesa.ambiente_nome}`,
          })),
      ];
    },
    atendimentoDestinoOptions() {
      return [
        { value: null, text: "Selecione" },
        ...this.allMesas
          .filter(
            (mesa) =>
              this.mesaSelecionada &&
              mesa.id !== this.mesaSelecionada.id &&
              mesa.status === "ocupada" &&
              mesa.atendimento_aberto_id
          )
          .map((mesa) => ({
            value: mesa.atendimento_aberto_id,
            text: `Mesa ${mesa.numero} - ${mesa.ambiente_nome} (#${mesa.atendimento_aberto_id})`,
          })),
      ];
    },
    transferableItens() {
      return this.itensPedidoAtual;
    },
    itemTransferOptions() {
      return [
        { value: null, text: "Selecione" },
        ...this.transferableItens.map((item) => ({
          value: item.id,
          text: `${this.produtoDescricao(item)} - ${Number(
            item.quantidade || 0
          ).toLocaleString("pt-BR")}`,
        })),
      ];
    },
    itemCheckboxOptions() {
      return this.transferableItens.map((item) => ({
        value: item.id,
        text: this.canShowFinancialInfo
          ? `${this.produtoDescricao(item)} - ${Number(
              item.quantidade || 0
            ).toLocaleString("pt-BR")} - ${this.formatMoney(item.valor_total)}`
          : `${this.produtoDescricao(item)} - ${Number(
              item.quantidade || 0
            ).toLocaleString("pt-BR")}`,
      }));
    },
  },
  mounted() {
    if (this.itsblocked) {
      this.$router.push({ name: "assinatura" });
      return;
    }

    this.getInit().then(() => {
      this.startEntregasAutoRefresh();
    });
  },
  beforeDestroy() {
    this.stopEntregasAutoRefresh();
    this.stopLayoutDrag();
  },
  methods: {
    ...mapActions(["setLoading"]),
    produtoOptionResumo(option) {
      const codigo = option.codigo || option.id;
      const resumo = `Código ${codigo}`;

      if (!this.canShowFinancialInfo) {
        return resumo;
      }

      return `${resumo} - ${this.formatMoney(this.productPrice(option))}`;
    },
    comandaResumoLabel(comanda) {
      const resumo = `${this.comandaLabel(comanda)} - ${
        comanda.quantidade_itens || 0
      } item(ns)`;

      if (!this.canShowFinancialInfo) {
        return resumo;
      }

      return `${resumo} - ${this.formatMoney(comanda.valor_total || 0)}`;
    },
    grupoItensResumoLabel(grupo) {
      const resumo = `${grupo.itens.length} item(ns)`;

      if (!this.canShowFinancialInfo) {
        return resumo;
      }

      return `${resumo} - ${this.formatMoney(grupo.valor_total)}`;
    },
    startEntregasAutoRefresh() {
      if (!this.canShowEntregaQueue) {
        return;
      }

      this.stopEntregasAutoRefresh();
      this.entregasRefreshInterval = setInterval(() => {
        // Pausa o polling quando a aba esta ociosa para nao gerar carga
        // desnecessaria no backend.
        if (document.hidden) {
          return;
        }
        this.getEntregasProntas(false);
      }, 30000);
    },
    stopEntregasAutoRefresh() {
      if (this.entregasRefreshInterval) {
        clearInterval(this.entregasRefreshInterval);
      }

      this.entregasRefreshInterval = null;
    },
    async getConfiguracoesRestaurante() {
      try {
        const { data } = await this.$http.post("/configuracoesgerais/find");
        const configuracao = Array.isArray(data) ? data[0] : data;
        this.restauranteUsarApenasComandas = Boolean(
          configuracao?.restaurante_usar_apenas_comandas
        );
        this.restauranteCardapioOnlineAtivo = Boolean(
          configuracao?.restaurante_cardapio_online_ativo
        );
      } catch (error) {
        this.restauranteUsarApenasComandas = false;
        this.restauranteCardapioOnlineAtivo = false;
      }

      this.inicializarVisualizacaoOperacao();
    },
    inicializarVisualizacaoOperacao() {
      // Preferencia do usuario/device (localStorage) com fallback no modo da
      // empresa. Permite trocar a visao no salao sem mexer em configuracoes.
      const preferida = this.lerVisualizacaoOperacaoStorage();
      if (preferida === "mesas" || preferida === "comandas") {
        this.visualizacaoOperacao = preferida;
        return;
      }

      this.visualizacaoOperacao = this.restauranteUsarApenasComandas
        ? "comandas"
        : "mesas";
    },
    lerVisualizacaoOperacaoStorage() {
      try {
        return window.localStorage.getItem(
          "restaurante_visualizacao_operacao"
        );
      } catch (error) {
        return null;
      }
    },
    salvarVisualizacaoOperacaoStorage(visao) {
      try {
        window.localStorage.setItem(
          "restaurante_visualizacao_operacao",
          visao
        );
      } catch (error) {
        // ignora falha de storage
      }
    },
    trocarVisualizacaoOperacao(visao) {
      if (visao !== "mesas" && visao !== "comandas") {
        return;
      }
      if (this.visualizacaoOperacao === visao) {
        return;
      }
      this.visualizacaoOperacao = visao;
      this.salvarVisualizacaoOperacaoStorage(visao);
      // Ao trocar para mesas, garante ambientes carregados (nao vinham no
      // modo comandas). Para comandas, so o refresh basta.
      if (visao === "mesas") {
        this.getInit();
      } else {
        this.refreshOperacao();
      }
    },
    async getComandasModo(showLoading = true) {
      try {
        if (showLoading) {
          this.comandasModoLoading = true;
        }

        const { data } = await this.$http.post("/restaurante/comandas/operacao", {
          search: this.comandasModoSearch || null,
          filtro: this.comandasModoFiltro,
          per_page: 60,
        });

        this.comandasModoMeta = data || null;
        this.comandasModo = Array.isArray(data) ? data : data?.data || [];
      } catch (error) {
        this.handleOperationError(error, "Não foi possível carregar comandas.");
      } finally {
        if (showLoading) {
          this.comandasModoLoading = false;
        }
      }
    },
    setComandasModoFiltro(filtro) {
      this.comandasModoFiltro = filtro;
      this.getComandasModo();
    },
    async openComandaModo(comanda) {
      if (!comanda?.id) {
        return;
      }

      try {
        this.comandaAvulsaLoading = true;
        const { data } = await this.$http.post(
          "/restaurante/comandas/operacao/find",
          { id: comanda.id }
        );
        this.setComandaAvulsaResponse(data);
        this.$bvModal.show("modalComandaAvulsaRestaurante");
      } catch (error) {
        this.handleOperationError(error, "Não foi possível abrir a comanda.");
      } finally {
        this.comandaAvulsaLoading = false;
      }
    },
    async reloadComandaModoSelecionada() {
      const comandaId = this.comandaAvulsaComanda?.id;

      if (!comandaId) {
        return;
      }

      const { data } = await this.$http.post(
        "/restaurante/comandas/operacao/find",
        { id: comandaId }
      );
      this.setComandaAvulsaResponse(data);
    },
    async cancelarComandaModoVazia() {
      const comanda = this.comandaAvulsaComanda;

      if (!comanda?.id || this.comandaAvulsaItens.length > 0) {
        return;
      }

      const result = await this.$swal.fire({
        icon: "warning",
        title: "Cancelar comanda vazia?",
        showCancelButton: true,
        confirmButtonText: "Cancelar comanda",
        cancelButtonText: "Voltar",
        confirmButtonColor: "#f64e60",
      });

      if (!result.isConfirmed) {
        return;
      }

      try {
        this.comandaAvulsaLoading = true;
        await this.$http.post("/restaurante/comandas/cancelar-vazia", {
          id: comanda.id,
        });
        this.$toast.success("Comanda vazia cancelada.");
        this.$bvModal.hide("modalComandaAvulsaRestaurante");
        this.comandaAvulsaData = null;
        await this.getComandasModo(false);
      } catch (error) {
        this.handleOperationError(error, "Não foi possível cancelar a comanda.");
      } finally {
        this.comandaAvulsaLoading = false;
      }
    },
    comandaModoTitulo(comanda) {
      const nome = String(comanda?.nome || "").trim();
      const codigo = String(comanda?.codigo || "").trim();

      if (nome && codigo) {
        return `${nome} (${codigo})`;
      }

      if (nome) {
        return `Comanda ${nome}`;
      }

      if (codigo) {
        return `Comanda ${codigo}`;
      }

      return comanda?.id ? `Comanda #${comanda.id}` : "Nova comanda";
    },
    comandaAbertaLabel(comanda) {
      const valor = comanda?.aberto_em || comanda?.created_at;

      if (!valor) {
        return "Aberta agora";
      }

      const data = new Date(valor);

      if (Number.isNaN(data.getTime())) {
        return "Aberta";
      }

      return `Aberta às ${data.toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
      })}`;
    },
    comandaEstadoLabel(comanda) {
      const itens = Number(comanda?.quantidade_itens_ativos || 0);
      const prontos = Number(comanda?.quantidade_itens_prontos || 0);
      const enviados = Number(comanda?.quantidade_itens_enviados || 0);
      const entregues = Number(comanda?.quantidade_itens_entregues || 0);

      if (itens === 0) {
        return "Sem itens";
      }

      if (prontos > 0) {
        return "Pronta";
      }

      if (enviados > 0) {
        return "Em produção";
      }

      if (entregues > 0) {
        return "Entregue";
      }

      return "Aberta";
    },
    comandaProducaoChips(comanda) {
      const itens = Number(comanda?.quantidade_itens_ativos || 0);
      const enviados = Number(comanda?.quantidade_itens_enviados || 0);
      const prontos = Number(comanda?.quantidade_itens_prontos || 0);
      const entregues = Number(comanda?.quantidade_itens_entregues || 0);

      if (itens === 0) {
        return [{ text: "Sem itens", class: "is-empty" }];
      }

      const chips = [{ text: `${itens} item(ns)`, class: "is-open" }];

      if (enviados > 0) {
        chips.push({ text: "Produção", class: "is-production" });
      }

      if (prontos > 0) {
        chips.push({ text: "Pronta", class: "is-ready" });
      }

      if (!enviados && !prontos && entregues > 0) {
        chips.push({ text: "Entregue", class: "is-ready" });
      }

      return chips;
    },
    setVisualizacaoSalao(tipo) {
      this.visualizacaoSalao = tipo;
      this.editandoLayout = false;
      this.stopLayoutDrag();
    },
    toggleLayoutEditor() {
      this.editandoLayout = !this.editandoLayout;
      this.visualizacaoSalao = "mapa";
      this.stopLayoutDrag();

      if (this.editandoLayout && !this.mesaLayoutSelecionadaId) {
        const primeiraMesa = this.ambientes
          .flatMap((ambiente) => ambiente.mesas)
          .find(Boolean);

        if (primeiraMesa) {
          this.selectMesaLayout(primeiraMesa);
        }
      }
    },
    layoutStorageKey(ambienteId) {
      return `restaurante_salao_layout_${ambienteId || "principal"}`;
    },
    loadStoredLayoutAmbiente(ambienteId) {
      try {
        return JSON.parse(
          window.localStorage.getItem(this.layoutStorageKey(ambienteId)) || "{}"
        );
      } catch (error) {
        return {};
      }
    },
    clearStoredLayoutAmbiente(ambienteId) {
      window.localStorage.removeItem(this.layoutStorageKey(ambienteId));
    },
    persistLayoutAmbiente(ambiente, mesas) {
      const layoutMap = mesas.reduce((acc, mesa) => {
        acc[mesa.id] = mesa.layout;
        return acc;
      }, {});

      window.localStorage.setItem(
        this.layoutStorageKey(ambiente.id),
        JSON.stringify(layoutMap)
      );
    },
    rawMesaLayout(mesa) {
      const layout = mesa.layout || mesa.layout_visual || {};

      return {
        x: layout.x ?? mesa.layout_x ?? mesa.posicao_x,
        y: layout.y ?? mesa.layout_y ?? mesa.posicao_y,
        width: layout.width ?? mesa.layout_width ?? mesa.largura_layout,
        height: layout.height ?? mesa.layout_height ?? mesa.altura_layout,
        shape:
          layout.shape || mesa.layout_shape || mesa.formato_layout || "rect",
      };
    },
    defaultMesaLayout(index, ambiente) {
      const total = Math.max(ambiente?.mesas?.length || 1, 1);
      const width = total <= 4 ? 12 : total <= 12 ? 10 : 8;
      const height = total <= 4 ? 15 : total <= 12 ? 13 : 11;
      const gap = total <= 4 ? 4 : 3;
      const maxColumns = Math.max(1, Math.floor(86 / (width + gap)));
      const idealColumns = Math.ceil(Math.sqrt(total * 1.45));
      const colunas = Math.min(total, maxColumns, Math.max(1, idealColumns));
      const coluna = index % colunas;
      const linha = Math.floor(index / colunas);

      return {
        x: 8 + coluna * (width + gap),
        y: 10 + linha * (height + 5),
        width,
        height,
        shape: "rect",
      };
    },
    clampLayoutNumber(value, min, max, fallback) {
      const number = Number(value);

      if (!Number.isFinite(number)) {
        return fallback;
      }

      return Math.min(Math.max(number, min), max);
    },
    roundLayoutNumber(value) {
      return Math.round(Number(value) * 10) / 10;
    },
    mesaNumeroSortValue(mesa) {
      const numero = String(mesa?.numero ?? "").trim();
      const numeric = Number(numero.replace(",", "."));

      if (numero !== "" && Number.isFinite(numeric)) {
        return {
          numeric,
          text: numero,
        };
      }

      return {
        numeric: Number.MAX_SAFE_INTEGER,
        text: numero,
      };
    },
    compareMesasByNumero(a, b) {
      const mesaA = this.mesaNumeroSortValue(a);
      const mesaB = this.mesaNumeroSortValue(b);

      if (mesaA.numeric !== mesaB.numeric) {
        return mesaA.numeric - mesaB.numeric;
      }

      return mesaA.text.localeCompare(mesaB.text, "pt-BR", {
        numeric: true,
        sensitivity: "base",
      });
    },
    sortMesas(mesas) {
      return [...(mesas || [])].sort((a, b) => this.compareMesasByNumero(a, b));
    },
    mesasAmbienteFiltradas(ambiente) {
      const mesas = ambiente && Array.isArray(ambiente.mesas)
        ? ambiente.mesas
        : [];
      const termo = (this.buscaMesaSalao || "").toLowerCase().trim();
      if (!termo) {
        return mesas;
      }
      return mesas.filter((mesa) => {
        const numero = String(mesa.numero || "").toLowerCase();
        const nome = String(mesa.nome || "").toLowerCase();
        const cliente = String(mesa.cliente_nome || "").toLowerCase();
        return (
          numero.includes(termo) ||
          nome.includes(termo) ||
          cliente.includes(termo)
        );
      });
    },
    mesaOperacionalId(mesa) {
      return mesa?.mesa_operacional_id || mesa?.mesa_mae_id || mesa?.id;
    },
    mesaJuncaoOptionsFor(origemMesa, exigirAtendimento = false) {
      return [
        { value: null, text: "Selecione a mesa mãe" },
        ...this.allMesas
          .filter((mesa) => {
            if (!origemMesa || mesa.status === "inativa") {
              return false;
            }

            const origemOperacional = this.mesaOperacionalId(origemMesa);
            const destinoOperacional = this.mesaOperacionalId(mesa);

            if (
              mesa.id === origemMesa.id ||
              destinoOperacional === origemOperacional
            ) {
              return false;
            }

            if (exigirAtendimento) {
              return mesa.status === "ocupada" && mesa.atendimento_aberto_id;
            }

            return true;
          })
          .map((mesa) => ({
            value: mesa.id,
            text: `${this.mesaDisplayLabel(mesa)} - ${
              mesa.ambiente_nome
            } (${this.statusLabel(mesa.status)})`,
          })),
      ];
    },
    mesaTemVinculo(mesa) {
      return (
        Boolean(mesa?.mesa_mae_id) ||
        Boolean(mesa?.mesas_vinculadas && mesa.mesas_vinculadas.length)
      );
    },
    mesaVinculoResumo(mesa, compacto = false) {
      if (!mesa) {
        return "";
      }

      if (mesa.mesa_mae_id) {
        return `Mãe ${mesa.mesa_mae_numero || mesa.mesa_operacional_numero}`;
      }

      const vinculadas = mesa.mesas_vinculadas || [];

      if (!vinculadas.length) {
        return "";
      }

      const numeros = [mesa.numero, ...vinculadas.map((item) => item.numero)]
        .filter(Boolean);
      const exibidos = compacto && numeros.length > 3
        ? [...numeros.slice(0, 3), "..."]
        : numeros;

      return `Bloco ${exibidos.join(", ")}`;
    },
    mesaDisplayLabel(mesa) {
      if (!mesa) {
        return "Mesa";
      }

      if (mesa.mesa_mae_id) {
        return `Mesa ${mesa.mesa_mae_numero || mesa.mesa_operacional_numero}`;
      }

      const vinculadas = mesa.mesas_vinculadas || [];

      if (!vinculadas.length) {
        return `Mesa ${mesa.numero}`;
      }

      return `Mesa ${mesa.numero} + ${vinculadas
        .map((item) => item.numero)
        .join(", ")}`;
    },
    mesaVinculosLayout(ambiente) {
      const linhas = [];

      ambiente.mesas.forEach((mesa, index) => {
        const vinculadas = mesa.mesas_vinculadas || [];

        if (!vinculadas.length) {
          return;
        }

        const origemLayout = this.mesaLayout(mesa, index, ambiente);

        vinculadas.forEach((vinculada) => {
          const childIndex = ambiente.mesas.findIndex(
            (item) => item.id === vinculada.id
          );

          if (childIndex === -1) {
            return;
          }

          const child = ambiente.mesas[childIndex];
          const childLayout = this.mesaLayout(child, childIndex, ambiente);

          linhas.push({
            key: `${mesa.id}-${child.id}`,
            x1: origemLayout.x + origemLayout.width / 2,
            y1: origemLayout.y + origemLayout.height / 2,
            x2: childLayout.x + childLayout.width / 2,
            y2: childLayout.y + childLayout.height / 2,
          });
        });
      });

      return linhas;
    },
    normalizeAmbientes(data) {
      return (data || []).map((ambiente) => ({
        ...ambiente,
        mesas: this.sortMesas(ambiente.mesas),
      }));
    },
    normalizeMesaLayout(rawLayout, index, ambiente) {
      const base = this.defaultMesaLayout(index, ambiente);
      const width = this.clampLayoutNumber(rawLayout?.width, 8, 18, base.width);
      const height = this.clampLayoutNumber(
        rawLayout?.height,
        10,
        20,
        base.height
      );
      const x = this.clampLayoutNumber(rawLayout?.x, 2, 98 - width, base.x);
      const y = this.clampLayoutNumber(rawLayout?.y, 2, 96 - height, base.y);
      const shape = rawLayout?.shape === "round" ? "round" : "rect";

      return {
        x: this.roundLayoutNumber(x),
        y: this.roundLayoutNumber(y),
        width: this.roundLayoutNumber(width),
        height: this.roundLayoutNumber(height),
        shape,
      };
    },
    hydrateLayoutDrafts() {
      this.ambientes.forEach((ambiente) => {
        const storedLayouts = this.loadStoredLayoutAmbiente(ambiente.id);

        ambiente.mesas.forEach((mesa, index) => {
          const rawLayout =
            this.layoutDrafts[mesa.id] ||
            (this.hasPersistedMesaLayout(mesa)
              ? this.rawMesaLayout(mesa)
              : storedLayouts[mesa.id] || this.rawMesaLayout(mesa));
          const layout = this.normalizeMesaLayout(rawLayout, index, ambiente);

          this.$set(this.layoutDrafts, mesa.id, layout);
        });
      });
    },
    hasPersistedMesaLayout(mesa) {
      const layout = mesa.layout || mesa.layout_visual || {};

      return (
        layout.x !== undefined ||
        layout.y !== undefined ||
        (mesa.layout_x !== undefined && mesa.layout_x !== null) ||
        (mesa.layout_y !== undefined && mesa.layout_y !== null)
      );
    },
    mesaLayout(mesa, index, ambiente) {
      return this.normalizeMesaLayout(
        this.layoutDrafts[mesa.id] || this.rawMesaLayout(mesa),
        index,
        ambiente
      );
    },
    mesaMapStyle(mesa, index, ambiente) {
      const layout = this.mesaLayout(mesa, index, ambiente);

      return {
        left: `${layout.x}%`,
        top: `${layout.y}%`,
        width: `${layout.width}%`,
        height: `${layout.height}%`,
      };
    },
    mesaShape(mesa) {
      return (
        this.layoutDrafts[mesa.id]?.shape || this.rawMesaLayout(mesa).shape
      );
    },
    selectedLayoutMesa(ambiente) {
      return ambiente.mesas.find(
        (mesa) => mesa.id === this.mesaLayoutSelecionadaId
      );
    },
    mesaLayoutSelecionadaLabel(ambiente) {
      const mesa = this.selectedLayoutMesa(ambiente);

      return mesa ? `Mesa ${mesa.numero}` : "Nenhuma";
    },
    selectMesaLayout(mesa) {
      this.mesaLayoutSelecionadaId = mesa.id;
    },
    findMesaLayoutContext(mesaId) {
      for (const ambiente of this.ambientes) {
        const index = ambiente.mesas.findIndex((mesa) => mesa.id === mesaId);

        if (index !== -1) {
          return {
            ambiente,
            mesa: ambiente.mesas[index],
            index,
          };
        }
      }

      return null;
    },
    updateMesaLayout(mesaId, rawLayout) {
      const context = this.findMesaLayoutContext(mesaId);

      if (!context) {
        return;
      }

      this.$set(
        this.layoutDrafts,
        mesaId,
        this.normalizeMesaLayout(rawLayout, context.index, context.ambiente)
      );
    },
    handleMesaMapClick(mesa) {
      if (this.editandoLayout) {
        this.selectMesaLayout(mesa);
        return;
      }

      this.handleMesaClick(mesa);
    },
    startLayoutDrag(event, mesa, ambiente) {
      if (!this.editandoLayout) {
        return;
      }

      if (event.button !== undefined && event.button !== 0) {
        return;
      }

      const index = ambiente.mesas.findIndex((item) => item.id === mesa.id);
      const mapElement = event.currentTarget.closest(".salao-map");

      if (!mapElement) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      this.selectMesaLayout(mesa);
      this.layoutDrag = {
        mesaId: mesa.id,
        startX: event.clientX,
        startY: event.clientY,
        mapRect: mapElement.getBoundingClientRect(),
        startLayout: this.mesaLayout(mesa, index, ambiente),
      };

      window.addEventListener("pointermove", this.onLayoutDragMove);
      window.addEventListener("pointerup", this.stopLayoutDrag);
      window.addEventListener("pointercancel", this.stopLayoutDrag);
    },
    onLayoutDragMove(event) {
      if (!this.layoutDrag) {
        return;
      }

      const deltaX =
        ((event.clientX - this.layoutDrag.startX) /
          this.layoutDrag.mapRect.width) *
        100;
      const deltaY =
        ((event.clientY - this.layoutDrag.startY) /
          this.layoutDrag.mapRect.height) *
        100;

      this.updateMesaLayout(this.layoutDrag.mesaId, {
        ...this.layoutDrag.startLayout,
        x: this.layoutDrag.startLayout.x + deltaX,
        y: this.layoutDrag.startLayout.y + deltaY,
      });
    },
    stopLayoutDrag() {
      if (typeof window !== "undefined") {
        window.removeEventListener("pointermove", this.onLayoutDragMove);
        window.removeEventListener("pointerup", this.stopLayoutDrag);
        window.removeEventListener("pointercancel", this.stopLayoutDrag);
      }

      this.layoutDrag = null;
    },
    nudgeSelectedMesa(ambiente, deltaX, deltaY) {
      const mesa = this.selectedLayoutMesa(ambiente);

      if (!mesa) {
        return;
      }

      const layout = this.layoutDrafts[mesa.id];
      this.updateMesaLayout(mesa.id, {
        ...layout,
        x: layout.x + deltaX,
        y: layout.y + deltaY,
      });
    },
    resizeSelectedMesa(ambiente, deltaWidth, deltaHeight) {
      const mesa = this.selectedLayoutMesa(ambiente);

      if (!mesa) {
        return;
      }

      const layout = this.layoutDrafts[mesa.id];
      this.updateMesaLayout(mesa.id, {
        ...layout,
        width: layout.width + deltaWidth,
        height: layout.height + deltaHeight,
      });
    },
    toggleSelectedMesaShape(ambiente) {
      const mesa = this.selectedLayoutMesa(ambiente);

      if (!mesa) {
        return;
      }

      const layout = this.layoutDrafts[mesa.id];
      this.updateMesaLayout(mesa.id, {
        ...layout,
        shape: layout.shape === "round" ? "rect" : "round",
      });
    },
    autoArrangeAmbiente(ambiente) {
      ambiente.mesas.forEach((mesa, index) => {
        this.$set(
          this.layoutDrafts,
          mesa.id,
          this.defaultMesaLayout(index, ambiente)
        );
      });

      if (ambiente.mesas.length) {
        this.selectMesaLayout(ambiente.mesas[0]);
      }
    },
    mesaCapacidadeLabel(mesa) {
      const ocupantes = Number(
        mesa.ocupantes ||
          mesa.quantidade_pessoas ||
          mesa.pessoas ||
          mesa.clientes_count ||
          0
      );
      const capacidade = Number(mesa.capacidade || 0);

      if (ocupantes && capacidade) {
        return `${ocupantes}/${capacidade} pessoa(s)`;
      }

      if (ocupantes) {
        return `${ocupantes} pessoa(s)`;
      }

      if (capacidade) {
        return `${capacidade} lugar(es)`;
      }

      return "Capacidade livre";
    },
    mesaGarcomLabel(mesa) {
      const garcom =
        mesa.garcom_nome ||
        mesa.vendedor_nome ||
        mesa.usuario_nome ||
        mesa.responsavel_nome;

      return garcom ? `Garçom: ${garcom}` : "";
    },
    mesaValorPendente(mesa) {
      return Number(
        mesa.valor_pendente ||
          mesa.valor_pendente_atendimento ||
          mesa.valor_total_pendente ||
          mesa.valor_total_atendimento ||
          0
      );
    },
    mesaResumoProducao(mesa) {
      const pendentes = Number(
        mesa.producao_pendente ||
          mesa.itens_producao_pendente ||
          mesa.quantidade_producao_pendente ||
          0
      );
      const prontos = Number(
        mesa.producao_pronta ||
          mesa.itens_prontos ||
          mesa.quantidade_producao_pronta ||
          0
      );

      if (!pendentes && !prontos) {
        return "";
      }

      return `${pendentes} prod. / ${prontos} pronto(s)`;
    },
    async saveLayoutAmbiente(ambiente) {
      const mesas = ambiente.mesas.map((mesa, index) => ({
        id: mesa.id,
        layout: this.mesaLayout(mesa, index, ambiente),
      }));

      try {
        this.setLoading(true);
        await this.$http.post("/restaurante/salao/update-layout", {
          ambiente_id: ambiente.id,
          mesas,
        });
        this.clearStoredLayoutAmbiente(ambiente.id);
        this.$toast.success("Layout do salão salvo.");
        await this.getSalao(false);
      } catch (error) {
        this.persistLayoutAmbiente(ambiente, mesas);
        this.$toast.info(
          "Layout salvo neste navegador. O servidor ainda não confirmou a gravação."
        );
      } finally {
        this.setLoading(false);
      }
    },
    async getInit() {
      // try/finally garante que setLoading(false) rode mesmo se algum await
      // lancar (antes o loading podia ficar preso para sempre).
      try {
        this.setLoading(true);
        await this.getConfiguracoesRestaurante();

        if (this.modoApenasComandas) {
          const tasks = [this.getComandasModo(false)];

          if (this.canShowEntregaQueue) {
            tasks.push(this.getEntregasProntas(false));
          }

          await Promise.all(tasks);
          return;
        }

        const tasks = [this.getAmbientes(), this.getSalao(false)];

        if (this.canShowEntregaQueue) {
          tasks.push(this.getEntregasProntas(false));
        }

        await Promise.all(tasks);
      } catch (error) {
        this.$toast.error("Não foi possível carregar os dados do salão.");
      } finally {
        this.setLoading(false);
      }
    },
    todayInputDate() {
      const date = new Date();
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");

      return `${year}-${month}-${day}`;
    },
    resetReservaForm() {
      this.reservaForm = {
        nome: "",
        data: this.todayInputDate(),
        horario: "",
        horario_fim: "",
        mesa_ids: [],
        quantidade_pessoas: null,
      };
    },
    async openReservasModal() {
      if (!this.reservaForm.data) {
        this.resetReservaForm();
      }

      this.$bvModal.show("modalReservasRestaurante");
      await this.getReservas();
    },
    async getReservas() {
      try {
        this.reservasLoading = true;
        const { data } = await this.$http.post("/restaurante/reservas", {
          data: this.reservaForm.data || this.todayInputDate(),
        });
        this.reservas = data || [];
      } catch (error) {
        this.handleOperationError(error, "Não foi possível carregar reservas.");
      } finally {
        this.reservasLoading = false;
      }
    },
    async createReserva() {
      const nome = String(this.reservaForm.nome || "").trim();

      if (!nome) {
        this.$toast.warning("Informe o nome da reserva.");
        return;
      }

      if (
        !this.reservaForm.data ||
        !this.reservaForm.horario ||
        !this.reservaForm.horario_fim
      ) {
        this.$toast.warning("Informe data, início e fim da reserva.");
        return;
      }

      if (this.reservaForm.horario_fim <= this.reservaForm.horario) {
        this.$toast.warning("O horário final deve ser maior que o inicial.");
        return;
      }

      if (!this.reservaForm.mesa_ids.length) {
        this.$toast.warning("Selecione pelo menos uma mesa para a reserva.");
        return;
      }

      try {
        this.reservasLoading = true;
        await this.$http.post("/restaurante/reservas/create", {
          ...this.reservaForm,
          nome,
        });
        this.$toast.success("Reserva criada.");
        this.resetReservaForm();
        await this.getReservas();
        await this.getSalao(false);
      } catch (error) {
        this.handleOperationError(error, "Não foi possível criar a reserva.");
      } finally {
        this.reservasLoading = false;
      }
    },
    async cancelReserva(reserva) {
      const result = await this.$swal.fire({
        icon: "warning",
        title: "Cancelar reserva?",
        showCancelButton: true,
        confirmButtonText: "Cancelar reserva",
        cancelButtonText: "Voltar",
        confirmButtonColor: "#f64e60",
      });

      if (!result.isConfirmed) {
        return;
      }

      try {
        this.reservasLoading = true;
        await this.$http.post("/restaurante/reservas/cancel", {
          id: reserva.id,
        });
        this.$toast.success("Reserva cancelada.");
        await this.getReservas();
        await this.getSalao(false);
      } catch (error) {
        this.handleOperationError(
          error,
          "Não foi possível cancelar a reserva."
        );
      } finally {
        this.reservasLoading = false;
      }
    },
    isMesaSelecionadaReserva(mesa) {
      return this.reservaForm.mesa_ids.includes(mesa.id);
    },
    toggleMesaReserva(mesa) {
      if (this.mesaReservaIndisponivel(mesa)) {
        return;
      }

      const mesaIds = [...this.reservaForm.mesa_ids];
      const index = mesaIds.indexOf(mesa.id);

      if (index === -1) {
        mesaIds.push(mesa.id);
      } else {
        mesaIds.splice(index, 1);
      }

      this.$set(this.reservaForm, "mesa_ids", mesaIds);
    },
    reservaDateKey(value) {
      return value ? String(value).slice(0, 10) : "";
    },
    reservaMesaIds(reserva) {
      if (Array.isArray(reserva?.mesas) && reserva.mesas.length) {
        return reserva.mesas.map((mesa) => mesa.id);
      }

      if (reserva?.mesa_id) {
        return [reserva.mesa_id];
      }

      return reserva?.mesa?.id ? [reserva.mesa.id] : [];
    },
    reservaConflitaComHorario(reserva) {
      if (
        reserva?.status !== "ativa" ||
        !this.reservaForm.data ||
        !this.reservaForm.horario ||
        !this.reservaForm.horario_fim
      ) {
        return false;
      }

      if (this.reservaDateKey(reserva.data) !== this.reservaForm.data) {
        return false;
      }

      const inicio = this.formatReservaHorario(reserva.horario);
      const fim = this.formatReservaHorario(
        reserva.horario_fim || reserva.horario
      );

      return (
        inicio < this.reservaForm.horario_fim && fim > this.reservaForm.horario
      );
    },
    reservaConflitanteMesa(mesa) {
      return this.reservas.find((reserva) => {
        return (
          this.reservaConflitaComHorario(reserva) &&
          this.reservaMesaIds(reserva).includes(mesa.id)
        );
      });
    },
    mesaReservaIndisponivel(mesa) {
      return !!this.reservaConflitanteMesa(mesa);
    },
    mesaReservaConflitoLabel(mesa) {
      const reserva = this.reservaConflitanteMesa(mesa);

      if (!reserva) {
        return "";
      }

      return `${this.formatReservaHorario(
        reserva.horario
      )}-${this.formatReservaHorario(reserva.horario_fim)}`;
    },
    async getAmbientes() {
      try {
        const { data } = await this.$http.post("/restaurante/ambientes/select");
        this.ambienteOptions = [
          { value: null, text: "Todos" },
          ...data.map((ambiente) => ({
            value: ambiente.id,
            text: ambiente.nome,
          })),
        ];
      } catch (error) {
        this.$toast.error("Não foi possível carregar os ambientes.");
      }
    },
    async getSalao(showLoading = true) {
      try {
        if (showLoading) {
          this.setLoading(true);
        }

        const payload = {};

        if (this.ambienteId) {
          payload.ambiente_id = this.ambienteId;
        }

        const { data } = await this.$http.post("/restaurante/salao", payload);
        this.ambientes = this.normalizeAmbientes(data);
        this.hydrateLayoutDrafts();
      } catch (error) {
        this.$toast.error("Não foi possível carregar as mesas.");
      } finally {
        if (showLoading) {
          this.setLoading(false);
        }
      }
    },
    async refreshOperacao(showLoading = true) {
      try {
        if (showLoading) {
          this.setLoading(true);
        }

        if (this.modoApenasComandas) {
          const tasks = [this.getComandasModo(false)];

          if (this.canShowEntregaQueue) {
            tasks.push(this.getEntregasProntas(false));
          }

          await Promise.all(tasks);
          return;
        }

        const tasks = [this.getSalao(false)];

        if (this.canShowEntregaQueue) {
          tasks.push(this.getEntregasProntas(false));
        }

        await Promise.all(tasks);
      } finally {
        if (showLoading) {
          this.setLoading(false);
        }
      }
    },
    async getEntregasProntas(showLoading = true) {
      if (!this.canShowEntregaQueue) {
        this.entregasProntas = [];
        return;
      }

      try {
        if (showLoading) {
          this.setLoading(true);
        }

        const { data } = await this.$http.post("/restaurante/producao/painel", {
          status: "pronto",
        });

        this.entregasProntas = this.filterEntregasByAmbiente(data).filter(
          (item) => !this.isPedidoOnlineProducao(item)
        );
      } catch (error) {
        this.$toast.error("Não foi possível carregar as entregas prontas.");
      } finally {
        if (showLoading) {
          this.setLoading(false);
        }
      }
    },
    filterEntregasByAmbiente(itens) {
      if (this.modoApenasComandas || !this.ambienteId) {
        return itens;
      }

      return itens.filter((item) => {
        const ambienteId =
          item.mesa?.ambiente_id || item.mesa?.ambiente?.id || null;

        return String(ambienteId) === String(this.ambienteId);
      });
    },
    entregaMesaLabel(item) {
      if (this.modoApenasComandas && !item.mesa) {
        return this.entregaComandaLabel(item) || "Comanda";
      }

      return item.mesa ? `Mesa ${item.mesa.numero}` : "Mesa não informada";
    },
    entregaSetorLabel(item) {
      return item.setor_producao ? item.setor_producao.nome : "Sem setor";
    },
    entregaProdutoDescricao(item) {
      return item.item && item.item.produto
        ? item.item.produto.descricao
        : `Produto #${item.item ? item.item.produto_id : "-"}`;
    },
    entregaQuantidadeLabel(item) {
      if (!item.item) {
        return "-";
      }

      const unidade =
        item.item.produto &&
        item.item.produto.unidade_medida &&
        item.item.produto.unidade_medida.sigla
          ? item.item.produto.unidade_medida.sigla
          : "";

      return `${Number(item.item.quantidade || 0).toLocaleString(
        "pt-BR"
      )} ${unidade}`;
    },
    entregaComandaLabel(item) {
      const comanda = this.entregaComandaData(item);
      const nome = comanda?.nome || item.item?.comanda_nome || "";
      const codigo = comanda?.codigo || "";

      if (this.modoApenasComandas) {
        if (nome && codigo) {
          return `Comanda ${nome} (${codigo})`;
        }

        if (nome) {
          return `Comanda ${nome}`;
        }

        if (codigo) {
          return `Comanda ${codigo}`;
        }
      }

      if (nome) {
        return nome;
      }

      return item.atendimento_id ? `Atendimento #${item.atendimento_id}` : "";
    },
    entregaTicketLabel(item) {
      return item.atendimento_id ? `Atendimento #${item.atendimento_id}` : "";
    },
    entregaTempoLabel(item) {
      const value = item.pronto_at || item.updated_at || item.enviado_at;

      if (!value) {
        return "agora";
      }

      const startedAt = new Date(value);
      const diff = Date.now() - startedAt.getTime();
      const minutos = Number.isFinite(diff)
        ? Math.max(Math.floor(diff / 60000), 0)
        : 0;

      if (minutos < 60) {
        return `${minutos} min`;
      }

      return `${Math.floor(minutos / 60)}h ${minutos % 60}m`;
    },
    entregaItemTimestamp(item) {
      const value =
        item.pronto_at || item.updated_at || item.enviado_at || item.created_at;
      const timestamp = value ? new Date(value).getTime() : Date.now();

      return Number.isFinite(timestamp) ? timestamp : Date.now();
    },
    entregaTicketKey(item) {
      return [
        item.atendimento_id || "atendimento",
        item.mesa?.id || item.mesa_id || "sem-mesa",
        item.setor_producao_id || item.setor_producao?.id || "sem-setor",
        this.entregaBatchKey(item),
      ].join(":");
    },
    entregaBatchKey(item) {
      const value =
        item.ticket_key ||
        item.pedido_producao_id ||
        item.lote_producao_id ||
        item.enviado_at ||
        item.item?.enviado_producao_at ||
        item.created_at ||
        item.id;

      return String(value || item.id || "sem-envio")
        .replace("T", " ")
        .slice(0, 19);
    },
    entregaTicketItems(item) {
      const key = this.entregaTicketKey(item);

      return this.entregasProntas.filter(
        (ticketItem) => this.entregaTicketKey(ticketItem) === key
      );
    },
    isEntregaTicketExpanded(ticket) {
      return this.expandedEntregaTickets[ticket.key] !== false;
    },
    toggleEntregaTicket(ticket) {
      this.$set(
        this.expandedEntregaTickets,
        ticket.key,
        !this.isEntregaTicketExpanded(ticket)
      );
    },
    isEntregaUpdating(item) {
      return (
        this.entregandoProducaoId === item.id ||
        this.entregandoProducaoId === this.entregaTicketKey(item)
      );
    },
    isEntregaTicketUpdating(ticket) {
      return (
        this.entregandoProducaoId === ticket.key ||
        ticket.items.some((item) => this.entregandoProducaoId === item.id)
      );
    },
    async refreshAfterEntrega() {
      if (this.modoApenasComandas) {
        await Promise.all([
          this.getEntregasProntas(false),
          this.getComandasModo(false),
        ]);
        return;
      }

      if (this.mesaSelecionada) {
        await this.refreshPainelAtendimento();
        return;
      }

      await Promise.all([this.getEntregasProntas(false), this.getSalao(false)]);
    },
    async marcarProducaoEntregue(item) {
      try {
        this.entregandoProducaoId = item.id;
        await this.$http.post("/restaurante/producao/update-status", {
          id: item.id,
          status: "entregue",
        });
        this.$toast.success("Item entregue!");
        await this.refreshAfterEntrega();
      } catch (error) {
        this.handleOperationError(error, "Não foi possível entregar o item.");
      } finally {
        this.entregandoProducaoId = null;
      }
    },
    async marcarEntregaTicket(ticket) {
      if (!ticket || !ticket.items.length) {
        return;
      }

      try {
        this.entregandoProducaoId = ticket.key;

        try {
          await this.$http.post("/restaurante/producao/bump", {
            ticket_key: ticket.key,
            ids: ticket.items.map((ticketItem) => ticketItem.id),
            status: "entregue",
          });
        } catch (error) {
          await Promise.all(
            ticket.items.map((ticketItem) =>
              this.$http.post("/restaurante/producao/update-status", {
                id: ticketItem.id,
                status: "entregue",
              })
            )
          );
        }

        this.$toast.success("Ticket entregue!");
        await this.refreshAfterEntrega();
      } catch (error) {
        this.handleOperationError(error, "Não foi possível entregar o ticket.");
      } finally {
        this.entregandoProducaoId = null;
      }
    },
    async marcarTicketEntregue(item) {
      const itens = this.entregaTicketItems(item);

      if (!itens.length) {
        return;
      }

      await this.marcarEntregaTicket({
        key: this.entregaTicketKey(item),
        items: itens,
      });
    },
    async openMesaFromEntrega(item) {
      if (this.modoApenasComandas && !item.mesa_id) {
        const comanda = this.entregaComandaData(item);

        if (!comanda?.id) {
          this.$toast.warning("Comanda não encontrada para este item.");
          return;
        }

        await this.openComandaModo(comanda);
        return;
      }

      const mesa = this.findMesaById(item.mesa_id) || item.mesa;

      if (!mesa) {
        this.$toast.warning("Mesa não encontrada para este item.");
        return;
      }

      await this.openPainelAtendimento({
        ...mesa,
        status: mesa.status || "ocupada",
        atendimento_aberto_id: item.atendimento_id,
      });
    },
    entregaComandaData(item) {
      return item.item?.comanda || item.comanda || null;
    },
    isPedidoOnlineProducao(item) {
      const comanda = this.entregaComandaData(item);
      const codigo = String(comanda?.codigo || "").trim();
      const observacao = String(item.observacao || "").trim();

      return (
        codigo.startsWith("ONLINE-") || observacao.startsWith("Pedido online ")
      );
    },
    async handleMesaClick(mesa) {
      if (mesa.status === "ocupada" || mesa.atendimento_aberto_id) {
        await this.openPainelAtendimento(mesa);
        return;
      }

      // Reservas da mesa (hoje). Se houver qualquer reserva, abre o modal
      // "Utilizar reserva?" que permite escolher o cliente (se >1) ou abrir
      // normalmente sem consumir reserva alguma.
      const reservasMesa = Array.isArray(mesa.reservas) ? mesa.reservas : [];
      const temReserva =
        reservasMesa.length > 0 ||
        mesa.reserva_atual ||
        mesa.reserva_dia ||
        mesa.proxima_reserva;

      if (temReserva) {
        this.abrirModalUtilizarReserva(mesa);
        return;
      }

      if (mesa.status === "reservada") {
        this.confirmOpenAtendimento(mesa);
        return;
      }

      if (mesa.status === "livre") {
        this.confirmOpenAtendimento(mesa);
        return;
      }

      this.$toast.info("Mesa inativa.");
    },
    abrirModalUtilizarReserva(mesa) {
      // Unifica as reservas vindas do backend (campo reservas[]) com os
      // marcadores individuais legados, sem duplicar.
      const reservas = Array.isArray(mesa.reservas) && mesa.reservas.length
        ? mesa.reservas
        : [mesa.reserva_atual, mesa.reserva_dia, mesa.proxima_reserva].filter(
            Boolean
          );

      const vistos = new Set();
      this.reservasParaUtilizar = reservas.filter((reserva) => {
        if (!reserva || !reserva.id || vistos.has(reserva.id)) {
          return false;
        }
        vistos.add(reserva.id);
        return true;
      });
      this.mesaParaAbrirReserva = mesa;
      this.reservaUtilizarSelecionada =
        this.reservasParaUtilizar.length === 1
          ? this.reservasParaUtilizar[0]
          : null;

      this.$bvModal.show("modalUtilizarReservaRestaurante");
    },
    confirmarUtilizarReserva() {
      const mesa = this.mesaParaAbrirReserva;
      const reserva = this.reservaUtilizarSelecionada || null;
      this.$bvModal.hide("modalUtilizarReservaRestaurante");
      this.confirmOpenAtendimento(mesa, reserva);
    },
    abrirSemUsarReserva() {
      const mesa = this.mesaParaAbrirReserva;
      this.$bvModal.hide("modalUtilizarReservaRestaurante");
      // Abre o atendimento SEM reserva_id: as reservas sao preservadas
      // (o backend so consome a reserva quando recebe reserva_id).
      this.confirmOpenAtendimento(mesa, null);
    },
    async confirmReservaAbertura(mesa, reserva) {
      const mesas = Array.isArray(reserva?.mesas) && reserva.mesas.length
        ? reserva.mesas.map((item) => item.numero).join(", ")
        : mesa.numero;
      const fim = reserva?.horario_fim
        ? ` às ${this.formatReservaHorario(reserva.horario_fim)}`
        : "";
      const result = await this.$swal.fire({
        icon: "info",
        title: "Mesa com reserva",
        text: `Mesa ${mesa.numero} pertence à reserva de ${reserva.nome} das ${this.formatReservaHorario(
          reserva.horario
        )}${fim}. Mesas: ${mesas}.`,
        showCancelButton: true,
        confirmButtonText: "Abrir atendimento",
        cancelButtonText: "Voltar",
        confirmButtonColor: "#3699ff",
      });

      return result.isConfirmed;
    },
    confirmOpenAtendimento(mesa, reserva = null) {
      this.mesaParaAbrir = mesa;
      this.reservaParaAbrir = reserva || mesa?.reserva_atual || null;
      this.clienteSelecionado = null;
      this.clientes = [];
      this.aberturaAtendimentoForm = {
        tipo: "nome",
        nome: this.reservaParaAbrir?.nome || "",
        codigo: "",
      };
      this.juntarMesaAberturaForm = {
        mesa_destino_id: null,
      };
      this.$bvModal.show("modalAbrirAtendimentoRestaurante");
      this.focusAberturaComandaInput();
    },
    focusAberturaComandaInput() {
      this.$nextTick(() => {
        if (
          this.aberturaAtendimentoForm.tipo === "nome" &&
          this.$refs.aberturaComandaNome
        ) {
          this.$refs.aberturaComandaNome.focus();
          return;
        }

        if (
          this.aberturaAtendimentoForm.tipo === "codigo" &&
          this.$refs.aberturaComandaCodigo
        ) {
          this.$refs.aberturaComandaCodigo.focus();
        }
      });
    },
    async openAtendimento() {
      try {
        const tipoAbertura = this.aberturaAtendimentoForm.tipo;
        const nomeComanda = String(
          this.aberturaAtendimentoForm.nome || ""
        ).trim();
        const codigoComanda = String(
          this.aberturaAtendimentoForm.codigo || ""
        ).trim();

        if (tipoAbertura === "nome" && !nomeComanda) {
          this.$toast.warning("Informe o nome da primeira comanda.");
          return;
        }

        if (tipoAbertura === "codigo" && !codigoComanda) {
          this.$toast.warning("Informe o código da comanda.");
          return;
        }

        this.abrindoAtendimento = true;
        this.setLoading(true);

        const payload = {
          mesa_id: this.mesaParaAbrir.id,
        };

        if (this.clienteSelecionado && this.clienteSelecionado.id_pessoa) {
          payload.id_pessoa = this.clienteSelecionado.id_pessoa;
        }

        if (tipoAbertura === "codigo") {
          payload.comanda_codigo = codigoComanda;
        } else {
          payload.comanda_nome = nomeComanda;
        }

        if (this.reservaAtualMesaParaAbrir?.id) {
          payload.reserva_id = this.reservaAtualMesaParaAbrir.id;
        }

        await this.$http.post("/restaurante/atendimentos/open", payload);
        this.$bvModal.hide("modalAbrirAtendimentoRestaurante");
        this.reservaParaAbrir = null;
        this.$toast.success("Atendimento aberto!");
        await this.getSalao(false);

        const mesaAtualizada = this.findMesaById(this.mesaParaAbrir.id);

        if (mesaAtualizada) {
          await this.openPainelAtendimento(mesaAtualizada, false);
        }
      } catch (error) {
        const message =
          error.response && error.response.data && error.response.data.msg
            ? error.response.data.msg
            : "Não foi possível abrir o atendimento.";
        this.$toast.error(message);
      } finally {
        this.abrindoAtendimento = false;
        this.setLoading(false);
      }
    },
    async juntarMesaNaAbertura() {
      if (!this.mesaParaAbrir || !this.juntarMesaAberturaForm.mesa_destino_id) {
        this.$toast.warning("Selecione a mesa mãe para juntar.");
        return;
      }

      try {
        this.juntandoMesaAbertura = true;
        this.setLoading(true);
        const { data } = await this.$http.post(
          "/restaurante/atendimentos/juntar-mesas",
          {
            mesa_origem_id: this.mesaParaAbrir.id,
            mesa_destino_id: this.juntarMesaAberturaForm.mesa_destino_id,
          }
        );

        this.$bvModal.hide("modalAbrirAtendimentoRestaurante");
        this.$toast.success("Mesa juntada ao bloco!");
        await this.getSalao(false);

        const mesaMae =
          this.findMesaById(data?.mesa_id) ||
          this.findMesaById(this.juntarMesaAberturaForm.mesa_destino_id);

        if (mesaMae) {
          await this.openPainelAtendimento(mesaMae, false);
        }
      } catch (error) {
        this.handleOperationError(error, "Não foi possível juntar a mesa.");
      } finally {
        this.juntandoMesaAbertura = false;
        this.setLoading(false);
      }
    },
    async openPainelAtendimento(mesa, pedirNomePrimeiraComanda = false) {
      try {
        this.setLoading(true);
        const mesaOperacionalId = this.mesaOperacionalId(mesa);
        const { data } = await this.$http.post(
          "/restaurante/atendimentos/find-open-by-mesa",
          {
            mesa_id: mesaOperacionalId,
          }
        );
        const mesaOperacional =
          this.findMesaById(data.mesa_id) ||
          this.findMesaById(mesaOperacionalId) ||
          mesa;

        this.mesaSelecionada = {
          ...mesaOperacional,
          atendimento_aberto_id: data.id,
          cliente_id: data.id_pessoa,
          cliente_nome: data.pessoa
            ? data.pessoa.nome
            : mesaOperacional.cliente_nome,
          valor_total_atendimento: data.valor_total,
          quantidade_itens: data.quantidade_itens,
        };
        this.atendimentoSelecionado = data;
        this.resetProdutoForm();
        await Promise.all([
          this.getComandasAtendimento(data.id),
          this.getItensAtendimento(data.id),
        ]);
        this.ensureComandaAtiva();
        this.$bvModal.show("modalAtendimentoRestaurante");

        // Carrega o cardapio rapido do garcom (cache em memoria do componente).
        // O componente tambem auto-carrega no proprio mounted(), mas mantemos
        // esta chamada com $nextTick como redundancia (condicoes de corrida do
        // modal do BootstrapVue podem atrasar a disponibilidade do $ref).
        this.$nextTick(() => {
          if (
            this.$refs.seletorProdutosGarcom &&
            typeof this.$refs.seletorProdutosGarcom.carregarCardapio ===
              "function"
          ) {
            this.$refs.seletorProdutosGarcom.carregarCardapio();
          }
        });

        if (pedirNomePrimeiraComanda) {
          this.openPrimeiraComandaModal();
        }
      } catch (error) {
        const message =
          error.response && error.response.data && error.response.data.msg
            ? error.response.data.msg
            : "Não foi possível abrir o painel do atendimento.";
        this.$toast.error(message);
      } finally {
        this.setLoading(false);
      }
    },
    async getItensAtendimento(atendimentoId = null) {
      const id = atendimentoId || this.atendimentoSelecionado?.id;

      if (!id) {
        this.itensAtendimento = [];
        return;
      }

      try {
        const { data } = await this.$http.post(
          "/restaurante/atendimentos/itens",
          {
            atendimento_id: id,
          }
        );
        this.itensAtendimento = data;
      } catch (error) {
        this.$toast.error("Não foi possível carregar os itens.");
      }
    },
    async getComandasAtendimento(atendimentoId = null) {
      const id = atendimentoId || this.atendimentoSelecionado?.id;

      if (!id) {
        this.comandas = [];
        this.comandaAtivaId = null;
        return;
      }

      try {
        const { data } = await this.$http.post("/restaurante/comandas", {
          atendimento_id: id,
        });
        this.comandas = this.normalizeComandasAtendimento(data);
        this.ensureComandaAtiva();
      } catch (error) {
        this.$toast.error("Não foi possível carregar as comandas.");
      }
    },
    normalizeComandasAtendimento(comandas) {
      const lista = comandas || [];
      const hasAvulsa = lista.some((comanda) => comanda.tipo === "avulso");

      if (!hasAvulsa) {
        return lista;
      }

      return lista.filter((comanda) => {
        const isComandaPadraoVazia =
          comanda.tipo === "mesa" &&
          Number(comanda.numero) === 1 &&
          String(comanda.nome || "").trim() === "Comanda 1" &&
          Number(comanda.quantidade_itens || 0) === 0 &&
          Number(comanda.valor_total || 0) === 0;

        return !isComandaPadraoVazia;
      });
    },
    async refreshPainelAtendimento() {
      if (!this.mesaSelecionada) {
        return;
      }

      try {
        // Usa o id operacional (mesa-mae de um bloco) para nao buscar o
        // atendimento pela mesa filha errada. Consistente com openPainel.
        const mesaId = this.mesaOperacionalId(this.mesaSelecionada);
        const { data } = await this.$http.post(
          "/restaurante/atendimentos/find-open-by-mesa",
          {
            mesa_id: mesaId,
          }
        );

        this.atendimentoSelecionado = data;
        this.mesaSelecionada = {
          ...this.mesaSelecionada,
          atendimento_aberto_id: data.id,
          cliente_id: data.id_pessoa,
          cliente_nome: data.pessoa
            ? data.pessoa.nome
            : this.mesaSelecionada.cliente_nome,
          valor_total_atendimento: data.valor_total,
          quantidade_itens: data.quantidade_itens,
        };

        const refreshTasks = [
          this.getComandasAtendimento(data.id),
          this.getItensAtendimento(data.id),
          this.getSalao(false),
        ];

        if (this.canShowEntregaQueue) {
          refreshTasks.push(this.getEntregasProntas(false));
        }

        await Promise.all(refreshTasks);
        this.ensureComandaAtiva();
      } catch (error) {
        // Erro no refresh apos uma acao bem-sucedida: loga sem mascarar o
        // sucesso anterior (ex.: item adicionado). Evita unhandled rejection.
        this.$toast.error("Não foi possível atualizar o painel do atendimento.");
      }
    },
    async addProdutoAtendimento() {
      if (!this.atendimentoSelecionado || !this.produtoSelecionado) {
        this.$toast.warning("Selecione um produto para lançar.");
        return;
      }

      if (Number(this.quantidadeProduto || 0) <= 0) {
        this.$toast.warning("Informe uma quantidade maior que zero.");
        return;
      }

      if (this.comandas.length > 1 && !this.comandaAtivaId) {
        this.$toast.warning("Selecione a comanda para lançar o produto.");
        return;
      }

      try {
        this.adicionandoProduto = true;
        const { data } = await this.$http.post(
          "/restaurante/atendimentos/itens/add",
          {
            atendimento_id: this.atendimentoSelecionado.id,
            comanda_id: this.comandaAtivaId,
            produto_id: this.produtoSelecionado.id,
            quantidade: this.quantidadeProduto,
            valor_unitario: this.productPrice(this.produtoSelecionado),
            desconto: this.descontoProduto || 0,
            observacao: this.observacaoProduto,
          }
        );
        this.$toast.success("Produto adicionado!");
        this.resetProdutoForm();
        await this.refreshPainelAtendimento();
      } catch (error) {
        const message =
          error.response && error.response.data && error.response.data.msg
            ? error.response.data.msg
            : "Não foi possível adicionar o produto.";
        this.$toast.error(message);
      } finally {
        this.adicionandoProduto = false;
      }
    },
    async updateItemAtendimento(item) {
      // Snapshot para rollback caso o update falhe (o v-model ja mutou o item).
      const snapshot = {
        quantidade: item.quantidade,
        desconto: item.desconto,
        observacao: item.observacao,
      };

      // Clamp de entrada: quantidade positiva; desconto nao negativo.
      item.quantidade = Math.max(0.0001, Number(item.quantidade) || 0);
      item.desconto = Math.max(0, Number(item.desconto) || 0);

      try {
        this.itemAtualizandoId = item.id;
        await this.$http.post("/restaurante/atendimentos/itens/update", {
          id: item.id,
          quantidade: item.quantidade,
          desconto: item.desconto || 0,
          observacao: item.observacao,
        });
        this.$toast.success("Item atualizado!");
        await this.refreshPainelAtendimento();
      } catch (error) {
        // Restaura os valores anteriores para nao divergir do backend.
        item.quantidade = snapshot.quantidade;
        item.desconto = snapshot.desconto;
        item.observacao = snapshot.observacao;
        const message =
          error.response && error.response.data && error.response.data.msg
            ? error.response.data.msg
            : "Não foi possível atualizar o item.";
        this.$toast.error(message);
      } finally {
        this.itemAtualizandoId = null;
      }
    },
    async cancelItemAtendimento(item) {
      const result = await this.$swal.fire({
        icon: "warning",
        title: "Cancelar item?",
        input: "text",
        inputPlaceholder: "Informe o motivo",
        showCancelButton: true,
        confirmButtonText: "Cancelar item",
        cancelButtonText: "Voltar",
        confirmButtonColor: "#f64e60",
        inputValidator: (value) => {
          if (!value) {
            return "Informe o motivo do cancelamento.";
          }

          return null;
        },
      });

      if (!result.isConfirmed) {
        return;
      }

      try {
        this.itemCancelandoId = item.id;
        await this.$http.post("/restaurante/atendimentos/itens/cancel", {
          id: item.id,
          motivo: result.value,
        });
        this.$toast.success("Item cancelado!");
        await this.refreshPainelAtendimento();
      } catch (error) {
        const message =
          error.response && error.response.data && error.response.data.msg
            ? error.response.data.msg
            : "Não foi possível cancelar o item.";
        this.$toast.error(message);
      } finally {
        this.itemCancelandoId = null;
      }
    },
    async sendItensProducao() {
      if (!this.atendimentoSelecionado) {
        return;
      }

      try {
        this.enviandoProducao = true;
        const { data } = await this.$http.post(
          "/restaurante/atendimentos/itens/send-production",
          {
            atendimento_id: this.atendimentoSelecionado.id,
          }
        );
        await this.imprimirTicketsProducao(data.impressao_producao);
        this.$toast.success("Itens enviados para produção!");
        await this.refreshPainelAtendimento();
      } catch (error) {
        const message =
          error.response && error.response.data && error.response.data.msg
            ? error.response.data.msg
            : "Não foi possível enviar os itens para produção.";
        this.$toast.error(message);
      } finally {
        this.enviandoProducao = false;
      }
    },
    async imprimirTicketsProducao(tickets) {
      if (!Array.isArray(tickets) || !tickets.length) {
        return;
      }

      try {
        await imprimirTicketsProducao(tickets);
      } catch (error) {
        this.$toast.warning(
          error.message || "Pedido enviado, mas não foi possível imprimir."
        );
      }
    },
    openTransferMesaModal() {
      this.transferMesaForm = {
        mesa_destino_id: null,
        juntar: false,
      };
      this.$bvModal.show("modalTransferirMesaRestaurante");
    },
    openTransferItemModal() {
      this.transferItemForm = {
        item_id: null,
        atendimento_destino_id: null,
      };
      this.$bvModal.show("modalTransferirItemRestaurante");
    },
    openSplitItensModal() {
      this.splitItensForm = {
        mesa_destino_id: null,
        item_ids: [],
      };
      this.$bvModal.show("modalSepararItensRestaurante");
    },
    openNovaComandaModal() {
      this.novaComandaForm = {
        nome: "",
        observacao: "",
      };
      this.$bvModal.show("modalNovaComandaRestaurante");
    },
    async confirmNovaComanda() {
      if (!this.atendimentoSelecionado) {
        return;
      }

      try {
        this.operacaoAtendimentoEmAndamento = true;
        const { data } = await this.$http.post("/restaurante/comandas/create", {
          atendimento_id: this.atendimentoSelecionado.id,
          nome: this.novaComandaForm.nome || null,
          observacao: this.novaComandaForm.observacao || null,
        });
        this.$toast.success("Comanda criada!");
        this.$bvModal.hide("modalNovaComandaRestaurante");
        await this.refreshPainelAtendimento();
        this.comandaAtivaId = data.id;
      } catch (error) {
        this.handleOperationError(error, "Não foi possível criar a comanda.");
      } finally {
        this.operacaoAtendimentoEmAndamento = false;
      }
    },
    openPrimeiraComandaModal() {
      const primeiraComanda =
        this.comandas.find((comanda) => Number(comanda.numero) === 1) ||
        this.comandas[0];

      if (!primeiraComanda) {
        return;
      }

      this.primeiraComandaParaNomear = primeiraComanda;
      this.primeiraComandaForm.nome = "";
      this.$bvModal.show("modalPrimeiraComandaRestaurante");

      this.$nextTick(() => {
        if (this.$refs.primeiraComandaNome) {
          this.$refs.primeiraComandaNome.focus();
        }
      });
    },
    async confirmPrimeiraComanda() {
      if (!this.primeiraComandaParaNomear) {
        return;
      }

      const nome = String(this.primeiraComandaForm.nome || "").trim();

      if (!nome) {
        this.$toast.warning("Informe o nome da primeira comanda.");
        return;
      }

      try {
        this.operacaoAtendimentoEmAndamento = true;
        const { data } = await this.$http.post("/restaurante/comandas/update", {
          id: this.primeiraComandaParaNomear.id,
          nome,
        });

        this.$toast.success("Primeira comanda definida!");
        this.$bvModal.hide("modalPrimeiraComandaRestaurante");
        this.primeiraComandaParaNomear = null;
        await this.refreshPainelAtendimento();
        this.comandaAtivaId = data.id;
      } catch (error) {
        this.handleOperationError(
          error,
          "Não foi possível definir a primeira comanda."
        );
      } finally {
        this.operacaoAtendimentoEmAndamento = false;
      }
    },
    openTransferComandaModal(item) {
      this.transferComandaForm = {
        item,
        comanda_destino_id: null,
      };
      this.$bvModal.show("modalTransferirComandaRestaurante");
    },
    openJuntarMesasModal() {
      this.juntarMesasForm = {
        mesa_destino_id: null,
      };
      this.$bvModal.show("modalJuntarMesasRestaurante");
    },
    async confirmJuntarMesas() {
      if (!this.juntarMesasForm.mesa_destino_id) {
        this.$toast.warning("Selecione a mesa para juntar.");
        return;
      }

      try {
        this.operacaoAtendimentoEmAndamento = true;
        await this.$http.post("/restaurante/atendimentos/juntar-mesas", {
          atendimento_id: this.atendimentoSelecionado.id,
          mesa_destino_id: this.juntarMesasForm.mesa_destino_id,
        });
        this.$toast.success("Mesas juntadas!");
        this.$bvModal.hide("modalJuntarMesasRestaurante");
        this.$bvModal.hide("modalAtendimentoRestaurante");
        await this.getSalao(false);
      } catch (error) {
        this.handleOperationError(error, "Não foi possível juntar as mesas.");
      } finally {
        this.operacaoAtendimentoEmAndamento = false;
      }
    },
    async confirmTransferComanda() {
      if (
        !this.transferComandaForm.item ||
        !this.transferComandaForm.comanda_destino_id
      ) {
        this.$toast.warning("Selecione a comanda destino.");
        return;
      }

      try {
        this.operacaoAtendimentoEmAndamento = true;
        await this.$http.post("/restaurante/comandas/transferir-item", {
          item_id: this.transferComandaForm.item.id,
          comanda_destino_id: this.transferComandaForm.comanda_destino_id,
        });
        this.$toast.success("Item transferido entre comandas!");
        this.$bvModal.hide("modalTransferirComandaRestaurante");
        await this.refreshPainelAtendimento();
      } catch (error) {
        this.handleOperationError(
          error,
          "Não foi possível transferir o item entre comandas."
        );
      } finally {
        this.operacaoAtendimentoEmAndamento = false;
      }
    },
    async confirmTransferMesa() {
      if (!this.transferMesaForm.mesa_destino_id) {
        this.$toast.warning("Selecione a mesa destino.");
        return;
      }

      try {
        this.operacaoAtendimentoEmAndamento = true;
        await this.$http.post("/restaurante/atendimentos/transfer-mesa", {
          atendimento_id: this.atendimentoSelecionado.id,
          mesa_destino_id: this.transferMesaForm.mesa_destino_id,
          juntar: this.transferMesaForm.juntar,
        });
        this.$toast.success("Mesa transferida!");
        this.$bvModal.hide("modalTransferirMesaRestaurante");
        this.$bvModal.hide("modalAtendimentoRestaurante");
        await this.getSalao(false);
      } catch (error) {
        this.handleOperationError(error, "Não foi possível transferir a mesa.");
      } finally {
        this.operacaoAtendimentoEmAndamento = false;
      }
    },
    async confirmTransferItem() {
      if (
        !this.transferItemForm.item_id ||
        !this.transferItemForm.atendimento_destino_id
      ) {
        this.$toast.warning("Selecione o item e o atendimento destino.");
        return;
      }

      try {
        this.operacaoAtendimentoEmAndamento = true;
        await this.$http.post("/restaurante/atendimentos/transfer-item", {
          item_id: this.transferItemForm.item_id,
          atendimento_destino_id: this.transferItemForm.atendimento_destino_id,
        });
        this.$toast.success("Item transferido!");
        this.$bvModal.hide("modalTransferirItemRestaurante");
        await this.refreshPainelAtendimento();
      } catch (error) {
        this.handleOperationError(error, "Não foi possível transferir o item.");
      } finally {
        this.operacaoAtendimentoEmAndamento = false;
      }
    },
    async confirmSplitItens() {
      if (!this.splitItensForm.item_ids.length) {
        this.$toast.warning("Selecione ao menos um item.");
        return;
      }

      if (!this.splitItensForm.mesa_destino_id) {
        this.$toast.warning("Selecione a mesa destino.");
        return;
      }

      try {
        this.operacaoAtendimentoEmAndamento = true;
        await this.$http.post("/restaurante/atendimentos/split-itens", {
          atendimento_id: this.atendimentoSelecionado.id,
          mesa_destino_id: this.splitItensForm.mesa_destino_id,
          item_ids: this.splitItensForm.item_ids,
        });
        this.$toast.success("Itens separados!");
        this.$bvModal.hide("modalSepararItensRestaurante");
        await this.refreshPainelAtendimento();
      } catch (error) {
        this.handleOperationError(error, "Não foi possível separar os itens.");
      } finally {
        this.operacaoAtendimentoEmAndamento = false;
      }
    },
    async cancelAtendimentoVazio() {
      const result = await this.$swal.fire({
        icon: "warning",
        title: "Cancelar atendimento vazio?",
        showCancelButton: true,
        confirmButtonText: "Sim",
        cancelButtonText: "Não",
        confirmButtonColor: "#f64e60",
      });

      if (!result.isConfirmed) {
        return;
      }

      try {
        this.setLoading(true);
        await this.$http.post("/restaurante/atendimentos/cancel-empty", {
          id: this.mesaSelecionada.atendimento_aberto_id,
        });
        this.$bvModal.hide("modalAtendimentoRestaurante");
        this.$toast.success("Atendimento cancelado!");
        await this.getSalao(false);
      } catch (error) {
        const message =
          error.response && error.response.data && error.response.data.msg
            ? error.response.data.msg
            : "Não foi possível cancelar o atendimento.";
        this.$toast.error(message);
      } finally {
        this.setLoading(false);
      }
    },
    openCheckout() {
      if (
        !this.mesaSelecionada ||
        !this.mesaSelecionada.atendimento_aberto_id
      ) {
        this.$toast.warning("Atendimento não encontrado para checkout.");
        return;
      }

      this.$bvModal.hide("modalAtendimentoRestaurante");
      this.$router.push({
        name: "restauranteCaixaCheckout",
        params: {
          atendimentoId: this.mesaSelecionada.atendimento_aberto_id,
        },
      });
    },
    onSearchCliente(search, loading) {
      if (!search.length) {
        return;
      }

      loading(true);
      this.searchCliente(loading, search, this);
    },
    searchCliente: _.debounce(async (loading, search, vm) => {
      try {
        vm.clientes = await vm.getPessoaNome(search);
      } finally {
        loading(false);
      }
    }, 450),
    onSearchProduto(search, loading) {
      if (search.length > 2 || /^[0-9]+$/.test(search)) {
        loading(true);
        this.searchProduto(loading, search, this);
      }
    },
    onQuickAddProdutoGarcom(produto) {
      // Toque curto no card: adiciona 1 unidade direto (caminho feliz, sem
      // abrir o formulario). Caso existam varias comandas, exige selecao.
      if (!this.atendimentoSelecionado || !produto) {
        return;
      }

      if (this.comandas.length > 1 && !this.comandaAtivaId) {
        this.$toast.warning("Selecione a comanda para lançar o produto.");
        this.onSelectProdutoGarcom(produto);
        return;
      }

      this.adicionarProdutoRapido(produto, 1, "");
    },
    async adicionarProdutoRapido(produto, quantidade, observacao) {
      if (!this.atendimentoSelecionado || !produto || Number(quantidade) <= 0) {
        return;
      }

      try {
        this.adicionandoProduto = true;
        await this.$http.post("/restaurante/atendimentos/itens/add", {
          atendimento_id: this.atendimentoSelecionado.id,
          comanda_id: this.comandaAtivaId,
          produto_id: produto.id,
          quantidade,
          valor_unitario: this.productPrice(produto),
          desconto: 0,
          observacao: observacao || null,
        });
        // Feedback no card (badge de quantidade) + toast curto.
        const idProd = produto.id;
        const atual = Number(this.quantidadesAdicionadasSessao[idProd] || 0);
        this.$set(this.quantidadesAdicionadasSessao, idProd, atual + quantidade);
        this.$toast.success(`${produto.descricao} adicionado`);
        await this.refreshPainelAtendimento();
      } catch (error) {
        const message =
          error.response && error.response.data && error.response.data.msg
            ? error.response.data.msg
            : "Não foi possível adicionar o produto.";
        this.$toast.error(message);
      } finally {
        this.adicionandoProduto = false;
      }
    },
    onSelectProdutoGarcom(produto) {
      // Toque longo / detalhe: abre o mini-form de quantidade/observacao para
      // lancar com ajustes (caminho para casos especiais).
      this.produtoSelecionado = produto;
      this.quantidadeProduto = this.quantidadeProduto || 1;
      this.$nextTick(() => {
        const ref = this.$refs.quantidadeProdutoInput;
        if (ref && typeof ref.focus === "function") {
          ref.focus();
        }
      });
    },
    searchProduto: _.debounce(async (loading, search, vm) => {
      try {
        const payload = {
          filter: search,
          typeFilter: 3,
          restaurante: true,
        };

        if (vm.mesaSelecionada?.cliente_id) {
          payload.id_pessoa = vm.mesaSelecionada.cliente_id;
        }

        if (vm.produtoDestino) {
          payload.restaurante_destino = vm.produtoDestino;
        }

        const { data } = await vm.$http.post("/produtos/filter-item", payload);
        vm.produtos = data;
      } finally {
        loading(false);
      }
    }, 350),
    onSearchProdutoAvulsa(search, loading) {
      if (search.length > 2 || /^[0-9]+$/.test(search)) {
        loading(true);
        this.searchProdutoAvulsa(loading, search, this);
      }
    },
    searchProdutoAvulsa: _.debounce(async (loading, search, vm) => {
      try {
        const { data } = await vm.$http.post("/produtos/filter-item", {
          filter: search,
          typeFilter: 3,
          restaurante: true,
        });
        vm.produtosAvulsa = data;
      } finally {
        loading(false);
      }
    }, 350),
    focusComandaAvulsaCodigo() {
      this.$nextTick(() => {
        if (this.$refs.codigoComandaAvulsa) {
          this.$refs.codigoComandaAvulsa.focus();
        }
      });
    },
    async openComandaAvulsaModal(vincularMesaAtual = false) {
      this.comandaAvulsaData = null;
      this.produtosAvulsa = [];
      this.quantidadesAdicionadasAvulsa = {};
      this.comandaAvulsaForm = {
        codigo: "",
        nome: "",
        mesa_id:
          vincularMesaAtual && this.mesaSelecionada
            ? this.mesaSelecionada.id
            : null,
        produto: null,
        quantidade: 1,
        desconto: 0,
        observacao: "",
      };
      this.$bvModal.show("modalComandaAvulsaRestaurante");
      if (this.modoApenasComandas) {
        return;
      }
      await this.getComandasAvulsasSemMesa();
    },
    async getComandasAvulsasSemMesa() {
      try {
        this.comandasAvulsasSemMesaLoading = true;
        const { data } = await this.$http.post(
          "/restaurante/comandas/avulsas/sem-mesa"
        );
        this.comandasAvulsasSemMesa = data || [];
      } catch (error) {
        this.handleOperationError(
          error,
          "Não foi possível carregar as comandas avulsas."
        );
      } finally {
        this.comandasAvulsasSemMesaLoading = false;
      }
    },
    selectComandaAvulsaSemMesa(avulsa) {
      this.setComandaAvulsaResponse(avulsa);
    },
    selectComandaAvulsaSemMesaById(comandaId) {
      const avulsa = this.comandasAvulsasSemMesa.find(
        (item) => Number(item.comanda.id) === Number(comandaId)
      );

      if (avulsa) {
        this.selectComandaAvulsaSemMesa(avulsa);
      }
    },
    comandaAvulsaListLabel(avulsa) {
      const codigo = avulsa?.comanda?.codigo || avulsa?.comanda?.id;
      return codigo ? `Avulsa ${codigo}` : "Comanda avulsa";
    },
    comandaAvulsaListResumo(avulsa) {
      const itens = avulsa?.itens || [];
      return `${itens.length} item(ns)`;
    },
    comandaAvulsaListTotal(avulsa) {
      const total = (avulsa?.itens || []).reduce(
        (acc, item) =>
          item.status === "cancelado"
            ? acc
            : acc + Number(item.valor_total || 0),
        0
      );
      return this.formatMoney(total);
    },
    setComandaAvulsaResponse(data) {
      this.comandaAvulsaData = data || null;

      if (data?.comanda?.codigo) {
        this.comandaAvulsaForm.codigo = data.comanda.codigo;
        this.comandaAvulsaSelecionadaId = data.comanda.id;
      }

      if (data?.comanda?.nome) {
        this.comandaAvulsaForm.nome = data.comanda.nome;
      }

      if (data?.mesa?.id || data?.comanda?.mesa_id) {
        this.comandaAvulsaForm.mesa_id = data?.mesa?.id || data.comanda.mesa_id;
      }
    },
    async buscarComandaAvulsa() {
      const codigo = String(this.comandaAvulsaForm.codigo || "").trim();
      const nome = String(this.comandaAvulsaForm.nome || "").trim();

      if (this.modoApenasComandas) {
        if (!codigo && !nome) {
          this.$toast.warning("Informe o código ou o nome da comanda.");
          return;
        }
      } else if (!codigo) {
        this.$toast.warning("Informe o código da comanda avulsa.");
        return;
      }

      try {
        this.comandaAvulsaLoading = true;
        const endpoint = this.modoApenasComandas
          ? "/restaurante/comandas/operacao/create"
          : "/restaurante/comandas/avulsas/find-or-create";
        const payload = this.modoApenasComandas
          ? { codigo, nome }
          : { codigo };
        const { data } = await this.$http.post(endpoint, payload);
        this.setComandaAvulsaResponse(data);
        if (this.modoApenasComandas) {
          this.$bvModal.show("modalComandaAvulsaRestaurante");
        }
        await this.refreshAfterComandaAvulsaChange(false);
      } catch (error) {
        this.handleOperationError(
          error,
          this.modoApenasComandas
            ? "Não foi possível carregar a comanda."
            : "Não foi possível carregar a comanda avulsa."
        );
      } finally {
        this.comandaAvulsaLoading = false;
      }
    },
    async addProdutoComandaAvulsa() {
      if (this.modoApenasComandas && !this.comandaAvulsaComanda?.id) {
        this.$toast.warning("Crie ou abra uma comanda antes de lançar itens.");
        return;
      }

      if (!this.modoApenasComandas && !this.comandaAvulsaForm.codigo) {
        this.$toast.warning("Informe o código da comanda avulsa.");
        return;
      }

      if (!this.comandaAvulsaForm.produto) {
        this.$toast.warning("Selecione um produto para lançar.");
        return;
      }

      if (Number(this.comandaAvulsaForm.quantidade || 0) <= 0) {
        this.$toast.warning("Informe uma quantidade maior que zero.");
        return;
      }

      try {
        this.comandaAvulsaLoading = true;
        const payload = {
          produto_id: this.comandaAvulsaForm.produto.id,
          quantidade: this.comandaAvulsaForm.quantidade,
          valor_unitario: this.productPrice(this.comandaAvulsaForm.produto),
          desconto: this.comandaAvulsaForm.desconto || 0,
          observacao: this.comandaAvulsaForm.observacao,
        };
        const endpoint = this.modoApenasComandas
          ? "/restaurante/comandas/operacao/itens/add"
          : "/restaurante/comandas/avulsas/itens/add";

        if (this.modoApenasComandas) {
          payload.comanda_id = this.comandaAvulsaComanda.id;
        } else {
          payload.codigo = this.comandaAvulsaForm.codigo;
        }

        const { data } = await this.$http.post(endpoint, payload);
        this.setComandaAvulsaResponse(data);
        this.comandaAvulsaForm.produto = null;
        this.produtosAvulsa = [];
        this.comandaAvulsaForm.quantidade = 1;
        this.comandaAvulsaForm.desconto = 0;
        this.comandaAvulsaForm.observacao = "";
        this.$toast.success(
          this.modoApenasComandas
            ? "Item lançado na comanda."
            : "Item lançado na comanda avulsa."
        );
        await this.refreshAfterComandaAvulsaChange();
      } catch (error) {
        this.handleOperationError(
          error,
          this.modoApenasComandas
            ? "Não foi possível lançar o item na comanda."
            : "Não foi possível lançar o item na comanda avulsa."
        );
      } finally {
        this.comandaAvulsaLoading = false;
      }
    },
    // --- Lancamento rapido via seletor de produtos (comanda avulsa) ---
    // Espelha onQuickAddProdutoGarcom / onSelectProdutoGarcom / adicionarProdutoRapido
    // do atendimento de mesa, mas usa o endpoint/payload da comanda avulsa.
    onQuickAddProdutoAvulsa(produto) {
      if (!produto) return;

      // Garante que ha uma comanda aberta para lancar o item.
      if (this.modoApenasComandas && !this.comandaAvulsaComanda?.id) {
        this.$toast.warning("Crie ou abra uma comanda antes de lançar itens.");
        this.onSelectProdutoAvulsa(produto);
        return;
      }

      if (!this.modoApenasComandas && !this.comandaAvulsaForm.codigo) {
        this.$toast.warning("Informe o código da comanda avulsa.");
        this.onSelectProdutoAvulsa(produto);
        return;
      }

      this.adicionarProdutoRapidoAvulsa(produto, 1, "");
    },
    onSelectProdutoAvulsa(produto) {
      if (!produto) return;
      // Preenche o VueSelect e foca o input de quantidade para ajuste manual.
      this.comandaAvulsaForm.produto = produto;
      this.comandaAvulsaForm.quantidade =
        this.comandaAvulsaForm.quantidade || 1;
      this.$nextTick(() => {
        const ref = this.$refs.quantidadeProdutoInputAvulsa;
        if (ref && typeof ref.focus === "function") ref.focus();
      });
    },
    async adicionarProdutoRapidoAvulsa(produto, quantidade, observacao) {
      if (!produto || Number(quantidade) <= 0) return;

      // Mesmas guardas de addProdutoComandaAvulsa.
      if (this.modoApenasComandas && !this.comandaAvulsaComanda?.id) {
        this.$toast.warning("Crie ou abra uma comanda antes de lançar itens.");
        return;
      }
      if (!this.modoApenasComandas && !this.comandaAvulsaForm.codigo) {
        this.$toast.warning("Informe o código da comanda avulsa.");
        return;
      }

      try {
        this.comandaAvulsaLoading = true;
        const payload = {
          produto_id: produto.id,
          quantidade,
          valor_unitario: this.productPrice(produto),
          desconto: 0,
          observacao: observacao || null,
        };
        const endpoint = this.modoApenasComandas
          ? "/restaurante/comandas/operacao/itens/add"
          : "/restaurante/comandas/avulsas/itens/add";

        if (this.modoApenasComandas) {
          payload.comanda_id = this.comandaAvulsaComanda.id;
        } else {
          payload.codigo = this.comandaAvulsaForm.codigo;
        }

        const { data } = await this.$http.post(endpoint, payload);
        this.setComandaAvulsaResponse(data);

        // Atualiza o badge de contagem do produto no seletor.
        const idProd = produto.id;
        const atual = Number(this.quantidadesAdicionadasAvulsa[idProd] || 0);
        this.$set(this.quantidadesAdicionadasAvulsa, idProd, atual + quantidade);

        this.$toast.success(`${produto.descricao} adicionado`);
        await this.refreshAfterComandaAvulsaChange();
      } catch (error) {
        this.handleOperationError(
          error,
          this.modoApenasComandas
            ? "Não foi possível lançar o item na comanda."
            : "Não foi possível lançar o item na comanda avulsa."
        );
      } finally {
        this.comandaAvulsaLoading = false;
      }
    },
    async vincularComandaAvulsaMesa() {
      if (!this.comandaAvulsaForm.codigo && !this.comandaAvulsaComanda?.id) {
        this.$toast.warning("Informe o código da comanda avulsa.");
        return;
      }

      if (!this.comandaAvulsaForm.mesa_id) {
        this.$toast.warning("Selecione a mesa para vincular.");
        return;
      }

      try {
        this.comandaAvulsaLoading = true;
        const { data } = await this.$http.post(
          "/restaurante/comandas/avulsas/vincular-mesa",
          {
            codigo: this.comandaAvulsaForm.codigo,
            comanda_id: this.comandaAvulsaComanda?.id || null,
            mesa_id: this.comandaAvulsaForm.mesa_id,
          }
        );
        this.setComandaAvulsaResponse(data);
        this.$toast.success("Comanda avulsa vinculada à mesa.");
        await this.refreshAfterComandaAvulsaChange();
      } catch (error) {
        this.handleOperationError(
          error,
          "Não foi possível vincular a comanda avulsa."
        );
      } finally {
        this.comandaAvulsaLoading = false;
      }
    },
    async sendProducaoComandaAvulsa() {
      const atendimentoId = this.comandaAvulsaData?.atendimento?.id;

      if (!atendimentoId) {
        return;
      }

      try {
        this.comandaAvulsaLoading = true;
        const { data } = await this.$http.post(
          "/restaurante/atendimentos/itens/send-production",
          {
            atendimento_id: atendimentoId,
          }
        );
        await this.imprimirTicketsProducao(data.impressao_producao);
        this.$toast.success("Itens enviados para produção.");
        if (this.modoApenasComandas) {
          await this.reloadComandaModoSelecionada();
          await this.getComandasModo(false);
        } else {
          await this.buscarComandaAvulsa();
        }
      } catch (error) {
        this.handleOperationError(
          error,
          "Não foi possível enviar a comanda para produção."
        );
      } finally {
        this.comandaAvulsaLoading = false;
      }
    },
    async cancelItemComandaAvulsa(item) {
      const result = await this.$swal.fire({
        icon: "warning",
        title: "Cancelar item?",
        input: "text",
        inputPlaceholder: "Informe o motivo",
        showCancelButton: true,
        confirmButtonText: "Cancelar item",
        cancelButtonText: "Voltar",
        confirmButtonColor: "#f64e60",
        inputValidator: (value) => {
          if (!value) {
            return "Informe o motivo do cancelamento.";
          }

          return null;
        },
      });

      if (!result.isConfirmed) {
        return;
      }

      try {
        this.comandaAvulsaLoading = true;
        await this.$http.post("/restaurante/atendimentos/itens/cancel", {
          id: item.id,
          motivo: result.value,
        });
        this.$toast.success("Item cancelado.");
        if (this.modoApenasComandas) {
          await this.reloadComandaModoSelecionada();
          await this.getComandasModo(false);
        } else {
          await this.buscarComandaAvulsa();
        }
      } catch (error) {
        this.handleOperationError(error, "Não foi possível cancelar o item.");
      } finally {
        this.comandaAvulsaLoading = false;
      }
    },
    async refreshAfterComandaAvulsaChange(refreshPainel = true) {
      if (this.modoApenasComandas) {
        await this.getComandasModo(false);
        return;
      }

      await this.getSalao(false);
      await this.getComandasAvulsasSemMesa();

      if (
        refreshPainel &&
        this.atendimentoSelecionado &&
        this.comandaAvulsaData?.atendimento?.id ===
          this.atendimentoSelecionado.id
      ) {
        await this.refreshPainelAtendimento();
      }

      if (this.canShowEntregaQueue) {
        await this.getEntregasProntas(false);
      }
    },
    openCheckoutComandaAvulsa() {
      if (this.modoApenasComandas) {
        const comanda = this.comandaAvulsaComanda;

        if (!comanda?.id || !comanda?.atendimento_id) {
          this.$toast.warning("Abra uma comanda antes de cobrar.");
          return;
        }

        this.$router.push({
          name: "restauranteCaixaCheckout",
          params: { atendimentoId: comanda.atendimento_id },
          query: { comanda_id: comanda.id, modo_comandas: "1" },
        });
        return;
      }

      const codigo =
        this.comandaAvulsaComanda?.codigo || this.comandaAvulsaForm.codigo;

      if (!codigo) {
        this.$toast.warning("Informe o código da comanda avulsa.");
        return;
      }

      this.$router.push({
        name: "restauranteCaixaCheckoutComanda",
        query: { codigo_comanda: codigo },
      });
    },
    findMesaById(id) {
      for (const ambiente of this.ambientes) {
        const mesa = ambiente.mesas.find((item) => item.id === id);

        if (mesa) {
          return mesa;
        }
      }

      return null;
    },
    selectedMesaDestinoLabel(id) {
      const mesa = this.allMesas.find((item) => item.id === id);

      if (!mesa) {
        return "Nenhuma mesa selecionada";
      }

      return `${this.mesaDisplayLabel(mesa)} - ${
        mesa.ambiente_nome
      } (${this.statusLabel(mesa.status)})`;
    },
    handleOperationError(error, fallback) {
      const message =
        error.response && error.response.data && error.response.data.msg
          ? error.response.data.msg
          : fallback;
      this.$toast.error(message);
    },
    isPedidoAtualItem(item) {
      return item.status === "aberto" && !item.enviado;
    },
    groupItensByComanda(itens) {
      const grupos = {};

      itens.forEach((item) => {
        const key = item.comanda_id || "sem_comanda";

        if (!grupos[key]) {
          grupos[key] = {
            comanda_id: item.comanda_id || null,
            label: this.itemComandaLabel(item),
            valor_total: 0,
            itens: [],
          };
        }

        grupos[key].itens.push(item);

        if (item.status !== "cancelado") {
          grupos[key].valor_total += Number(item.valor_total || 0);
        }
      });

      return Object.values(grupos).map((grupo) => ({
        ...grupo,
        valor_total: Number(grupo.valor_total.toFixed(2)),
      }));
    },
    resetProdutoForm() {
      this.produtoSelecionado = null;
      this.produtos = [];
      this.quantidadeProduto = 1;
      this.descontoProduto = 0;
      this.observacaoProduto = "";
      this.quantidadesAdicionadasSessao = {};
      this.ensureComandaAtiva();
    },
    productPrice(produto) {
      return Number(produto?.preco_lista_cliente || produto?.preco || 0);
    },
    produtoDescricao(item) {
      return item.produto
        ? item.produto.descricao
        : `Produto #${item.produto_id}`;
    },
    comandaLabel(comanda) {
      if (!comanda) {
        return "Sem comanda";
      }

      if (this.modoApenasComandas && comanda.tipo === "avulso") {
        return this.comandaModoTitulo(comanda);
      }

      if (comanda.tipo === "avulso" || (!comanda.nome && comanda.codigo)) {
        return `Avulsa ${comanda.codigo || comanda.id}`;
      }

      return comanda.nome || `Comanda ${comanda.numero || comanda.id}`;
    },
    itemComandaLabel(item) {
      if (item.comanda) {
        return this.comandaLabel(item.comanda);
      }

      const comanda = this.comandas.find(
        (comanda) => comanda.id === item.comanda_id
      );

      return this.comandaLabel(comanda);
    },
    setComandaAtiva(comandaId) {
      this.comandaAtivaId = comandaId;
    },
    ensureComandaAtiva() {
      if (!this.comandas.length) {
        this.comandaAtivaId = null;
        return;
      }

      const existe = this.comandas.some(
        (comanda) => comanda.id === this.comandaAtivaId
      );

      if (!existe) {
        const aberta = this.comandas.find(
          (comanda) => comanda.status === "aberta"
        );
        this.comandaAtivaId = aberta ? aberta.id : this.comandas[0].id;
      }
    },
    canEditItem(item) {
      return this.isPedidoAtualItem(item);
    },
    canCancelItem(item) {
      return this.canEditItem(item) || this.canCancelProductionItem(item);
    },
    canCancelProductionItem(item) {
      return ["enviado_producao", "preparando"].includes(item?.status);
    },
    canTransferirEntreComandas(item) {
      return this.isPedidoAtualItem(item) && this.comandas.length > 1;
    },
    statusItemLabel(status) {
      const labels = {
        aberto: "Aberto",
        enviado_producao: "Enviado",
        preparando: "Preparando",
        pronto: "Pronto",
        entregue: "Entregue",
        cancelado: "Cancelado",
      };

      return labels[status] || status;
    },
    statusItemClass(status) {
      const classes = {
        aberto: "label label-lg label-inline label-light-success",
        enviado_producao: "label label-lg label-inline label-light-warning",
        preparando: "label label-lg label-inline label-light-primary",
        pronto: "label label-lg label-inline label-light-info",
        entregue: "label label-lg label-inline label-light-dark",
        cancelado: "label label-lg label-inline label-light-danger",
      };

      return classes[status] || "label label-lg label-inline label-light";
    },
    statusLabel(status) {
      const labels = {
        livre: "Livre",
        ocupada: "Ocupada",
        reservada: "Reservada",
        inativa: "Inativa",
      };

      return labels[status] || status;
    },
    statusBadgeClass(status) {
      const classes = {
        livre: "label label-lg label-inline label-light-success",
        ocupada: "label label-lg label-inline label-light-danger",
        reservada: "label label-lg label-inline label-light-warning",
        inativa: "label label-lg label-inline label-light-dark",
      };

      return classes[status] || "label label-lg label-inline label-light";
    },
    formatMoney(value) {
      return Number(value || 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
    mesaReservaResumo(mesa, somenteAtual = false) {
      const reserva = somenteAtual
        ? mesa?.reserva_atual
        : mesa?.reserva_atual || mesa?.proxima_reserva || mesa?.reserva_dia;

      if (!reserva) {
        return "";
      }

      const prefixo = mesa.reserva_atual ? "Reserva agora" : "Reserva";
      const fim = reserva.horario_fim
        ? `-${this.formatReservaHorario(reserva.horario_fim)}`
        : "";

      return `${prefixo} ${this.formatReservaHorario(reserva.horario)}${fim}`;
    },
    mesaReservaMensagem(mesa) {
      const reserva =
        mesa?.reserva_atual || mesa?.proxima_reserva || mesa?.reserva_dia;

      if (!reserva) {
        return "";
      }

      const mesas = Array.isArray(reserva.mesas)
        ? reserva.mesas.map((item) => item.numero).join(", ")
        : mesa.numero;
      const prefixo = mesa.reserva_atual
        ? "Mesa reservada agora"
        : "Esta mesa tem reserva";
      const fim = reserva.horario_fim
        ? ` às ${this.formatReservaHorario(reserva.horario_fim)}`
        : "";

      return `${prefixo} para ${reserva.nome} das ${this.formatReservaHorario(
        reserva.horario
      )}${fim}. Mesas: ${mesas}.`;
    },
    formatReservaData(value) {
      if (!value) {
        return "-";
      }

      const date = String(value).slice(0, 10).split("-");

      if (date.length !== 3) {
        return value;
      }

      return `${date[2]}/${date[1]}/${date[0]}`;
    },
    formatReservaHorario(value) {
      return value ? String(value).slice(0, 5) : "-";
    },
    reservaMesaLabel(reserva) {
      if (Array.isArray(reserva?.mesas) && reserva.mesas.length) {
        return `Mesas ${reserva.mesas.map((mesa) => mesa.numero).join(", ")}`;
      }

      if (!reserva?.mesa) {
        return "Sem mesa";
      }

      return `Mesa ${reserva.mesa.numero}`;
    },
    formatQuantity(value) {
      return Number(value || 0).toLocaleString("pt-BR");
    },
  },
};
</script>

<style scoped>
.restaurante-salao .card-body {
  min-height: 420px;
}

.operacao-resumo {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  margin-bottom: 20px;
}

.operacao-metric {
  background: #fff;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  min-height: 104px;
  padding: 14px;
}

.operacao-metric span,
.operacao-metric small {
  color: #7e8299;
  display: block;
  font-weight: 600;
}

.operacao-metric strong {
  color: #181c32;
  display: block;
  font-size: 1.35rem;
  line-height: 1.25;
  margin: 6px 0;
}

.entrega-queue {
  border: 1px solid #ebedf3;
  border-left: 5px solid #1bc5bd;
  border-radius: 6px;
  margin-bottom: 20px;
  padding: 14px;
}

.entrega-queue-header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.entrega-queue-header h5 {
  color: #181c32;
  font-size: 1rem;
  font-weight: 800;
  margin: 0;
}

.entrega-queue-header span,
.entrega-queue-actions strong {
  color: #7e8299;
  font-weight: 700;
}

.entrega-queue-actions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.entrega-grid {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
}

.entrega-card {
  background: #f7f8fb;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  min-height: 188px;
  padding: 14px;
}

.entrega-card-top {
  align-items: flex-start;
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;
}

.entrega-ticket-toggle {
  background: transparent;
  border: 0;
  color: inherit;
  padding: 0;
  text-align: left;
  width: 100%;
}

.entrega-card-top div,
.entrega-meta {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.entrega-card-top strong,
.entrega-produto {
  color: #181c32;
  font-weight: 800;
}

.entrega-card-top span,
.entrega-meta span {
  color: #7e8299;
  font-weight: 700;
}

.entrega-ticket-summary {
  background: #fff;
  border-radius: 999px;
  color: #3f4254 !important;
  padding: 4px 8px;
  white-space: nowrap;
}

.entrega-card-top em {
  background: #fff;
  border-radius: 999px;
  color: #181c32;
  font-style: normal;
  font-weight: 800;
  padding: 4px 8px;
  white-space: nowrap;
}

.entrega-ticket-meta {
  margin-bottom: 10px;
}

.entrega-ticket-items {
  border-top: 1px solid #ebedf3;
  display: grid;
  gap: 10px;
  margin-top: 4px;
  padding-top: 12px;
}

.entrega-ticket-item {
  align-items: center;
  background: #fff;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  display: grid;
  gap: 12px;
  grid-template-columns: minmax(0, 1fr) auto;
  padding: 12px;
}

.entrega-produto {
  display: block;
  margin-bottom: 10px;
}

.entrega-observacao {
  background: #fff;
  border-radius: 6px;
  color: #3f4254;
  font-weight: 600;
  margin: 10px 0 0;
  padding: 8px 10px;
}

.entrega-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: auto;
  padding-top: 14px;
}

.salao-mode-group {
  background: #f3f6f9;
  border: 1px solid #e4e6ef;
  border-radius: 6px;
  display: grid;
  gap: 6px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  padding: 6px;
}

.salao-mode-button {
  background: transparent;
  border: 0;
  border-radius: 4px;
  color: #7e8299;
  font-weight: 700;
  min-height: 38px;
}

.salao-mode-button.active {
  background: #181c32;
  color: #fff;
}

.status-legenda {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 12px;
  padding-bottom: 1.75rem;
}

.status-legenda-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #7e8299;
  font-weight: 600;
}

.status-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.status-dot-livre {
  background: #1bc5bd;
}

.status-dot-ocupada {
  background: #f64e60;
}

.status-dot-reservada {
  background: #ffa800;
}

.status-dot-inativa {
  background: #7e8299;
}

.empty-state {
  background: #f3f6f9;
  border-radius: 6px;
  color: #7e8299;
  font-weight: 600;
  padding: 24px;
  text-align: center;
}

.ambiente-bloco + .ambiente-bloco {
  margin-top: 28px;
}

.ambiente-header {
  align-items: center;
  border-bottom: 1px solid #ebedf3;
  display: flex;
  justify-content: space-between;
  margin-bottom: 18px;
  padding-bottom: 10px;
}

.ambiente-header h4 {
  color: #181c32;
  font-size: 1.15rem;
  font-weight: 700;
  margin: 0;
}

.ambiente-header span {
  color: #7e8299;
}

.ambiente-actions {
  align-items: center;
  display: flex;
  gap: 10px;
}

.layout-editor-toolbar {
  align-items: center;
  background: #f3f6f9;
  border: 1px solid #e4e6ef;
  border-radius: 6px;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  margin-bottom: 14px;
  padding: 10px 12px;
}

.layout-editor-target {
  display: flex;
  flex-direction: column;
  min-width: 160px;
}

.layout-editor-target span {
  color: #7e8299;
  font-size: 0.78rem;
  font-weight: 800;
  text-transform: uppercase;
}

.layout-editor-target strong {
  color: #181c32;
  font-weight: 800;
}

.layout-editor-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: flex-end;
}

.salao-map {
  background: linear-gradient(
      90deg,
      rgba(126, 130, 153, 0.08) 1px,
      transparent 1px
    ),
    linear-gradient(180deg, rgba(126, 130, 153, 0.08) 1px, transparent 1px),
    #f7f8fb;
  background-size: 32px 32px;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  min-height: 540px;
  overflow: hidden;
  position: relative;
}

.mesa-vinculos-svg {
  inset: 0;
  pointer-events: none;
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.mesa-vinculos-svg line {
  stroke: #3699ff;
  stroke-dasharray: 8 8;
  stroke-linecap: round;
  stroke-width: 3;
}

.mesa-map-card {
  align-items: center;
  background: #fff;
  border: 2px solid #e4e6ef;
  border-radius: 8px;
  color: #181c32;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 78px;
  min-width: 118px;
  padding: 10px;
  position: absolute;
  text-align: center;
  transition: box-shadow 0.15s ease, transform 0.15s ease;
  user-select: none;
  z-index: 2;
}

.mesa-map-card:hover {
  box-shadow: 0 10px 24px rgba(82, 63, 105, 0.12);
  transform: translateY(-1px);
}

.mesa-map-card span {
  color: #7e8299;
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
}

.mesa-map-card strong {
  font-size: 1.4rem;
  line-height: 1.1;
}

.mesa-map-card small,
.mesa-map-card em {
  color: #3f4254;
  font-style: normal;
  font-weight: 700;
  line-height: 1.2;
  margin-top: 3px;
}

.mesa-map-card-livre {
  border-color: #1bc5bd;
}

.mesa-map-card-ocupada {
  border-color: #f64e60;
}

.mesa-map-card-reservada {
  background: #fff8dd;
  border-color: #ffa800;
}

.mesa-map-card-reserva-atual {
  background: #fff8dd;
  border-color: #ffa800;
  box-shadow: 0 0 0 5px rgba(255, 168, 0, 0.2);
}

.mesa-map-card-inativa {
  background: #eef0f5;
  border-color: #7e8299;
  cursor: not-allowed;
}

.mesa-map-card-round {
  border-radius: 999px;
}

.mesa-map-card-layout {
  cursor: grab;
  outline: 2px dashed rgba(54, 153, 255, 0.55);
  outline-offset: 4px;
}

.mesa-map-card-selected {
  box-shadow: 0 0 0 4px rgba(54, 153, 255, 0.18),
    0 10px 24px rgba(82, 63, 105, 0.12);
}

.mesa-map-card-dragging {
  cursor: grabbing;
  transform: scale(1.01);
  z-index: 3;
}

.mesa-map-card-linked {
  box-shadow: 0 0 0 4px rgba(54, 153, 255, 0.12);
}

.mesa-link-icon {
  background: #e1f0ff;
  border-radius: 999px;
  color: #3699ff;
  font-size: 0.72rem;
  padding: 5px;
  position: absolute;
  right: 8px;
  top: 8px;
}

.mesa-link-label {
  color: #3699ff !important;
  font-size: 0.7rem;
  max-width: calc(100% - 14px);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mesa-reserva-info {
  color: #b97800 !important;
  font-size: 0.7rem;
  max-width: calc(100% - 14px);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.reserva-chegada-box {
  background: #fff8dd;
  border: 1px solid rgba(255, 168, 0, 0.45);
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-bottom: 14px;
  padding: 12px 14px;
}

.reserva-chegada-box strong {
  color: #181c32;
  font-weight: 800;
}

.reserva-chegada-box span,
.reserva-chegada-box small {
  color: #7e6500;
  font-weight: 700;
}

.mesas-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
}

.mesa-card {
  align-items: flex-start;
  background: #fff;
  border: 1px solid #ebedf3;
  border-left-width: 5px;
  border-radius: 6px;
  color: #3f4254;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  min-height: 180px;
  padding: 16px;
  text-align: left;
  transition: box-shadow 0.15s ease, transform 0.15s ease;
}

.mesa-card:hover {
  box-shadow: 0 10px 24px rgba(82, 63, 105, 0.12);
  transform: translateY(-1px);
}

.mesa-card-topline {
  align-items: center;
  display: flex;
  gap: 6px;
  margin-bottom: 12px;
}

.mesa-card strong {
  color: #181c32;
  font-size: 1.2rem;
  line-height: 1.25;
  margin-bottom: 4px;
}

.mesa-card small,
.mesa-descricao {
  color: #7e8299;
  font-weight: 600;
}

.mesa-descricao {
  display: block;
  margin-top: 8px;
}

.mesa-status {
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 4px 8px;
  text-transform: uppercase;
}

.mesa-link-chip {
  align-items: center;
  background: #e1f0ff;
  border-radius: 4px;
  color: #3699ff;
  display: inline-flex;
  font-size: 0.75rem;
  font-weight: 800;
  gap: 5px;
  padding: 4px 8px;
}

.mesa-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: auto;
  padding-top: 12px;
  width: 100%;
}

.mesa-info span {
  color: #3f4254;
  font-size: 0.86rem;
  font-weight: 600;
}

.mesa-card-livre {
  border-left-color: #1bc5bd;
}

.mesa-card-livre .mesa-status {
  background: #c9f7f5;
  color: #1bc5bd;
}

.mesa-card-ocupada {
  border-left-color: #f64e60;
}

.mesa-card-ocupada .mesa-status {
  background: #ffe2e5;
  color: #f64e60;
}

.mesa-card-reservada {
  background: #fffdf5;
  border-left-color: #ffa800;
}

.mesa-card-reservada .mesa-status {
  background: #fff4de;
  color: #ffa800;
}

.mesa-card-reserva-atual {
  background: #fff8dd;
  border-color: rgba(255, 168, 0, 0.42);
  border-left-color: #ffa800;
}

.mesa-card-inativa {
  background: #f3f6f9;
  border-left-color: #7e8299;
  cursor: not-allowed;
}

.mesa-card-inativa .mesa-status {
  background: #e4e6ef;
  color: #7e8299;
}

.modal-atendimento-header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 18px;
}

.modal-atendimento-header strong {
  color: #181c32;
  font-size: 1.2rem;
}

.atendimento-shell {
  display: block;
}

.modal-atendimento-info {
  background: #f3f6f9;
  border-radius: 6px;
  display: grid;
  gap: 8px 18px;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  margin-bottom: 16px;
  padding: 14px 16px;
}

.modal-atendimento-info p {
  color: #3f4254;
  font-weight: 600;
  margin-bottom: 0;
}

.produto-atendimento-form {
  background: #f3f6f9;
  border-radius: 6px;
  margin-bottom: 20px;
  padding: 18px;
}

/* Altura controlada do seletor de produtos: toolbar fixo + grid rolavel */
.produto-atendimento-form .seletor-produtos-garcom {
  background: #fff;
  border-radius: 8px;
  padding: 12px;
  max-height: 420px;
}
.produto-atendimento-form .seletor-produtos-garcom .spg-corpo {
  max-height: 330px;
}

.reservas-list {
  border: 1px solid #ebedf3;
  border-radius: 6px;
  margin-top: 12px;
  overflow: hidden;
}

.reserva-selecao-resumo {
  background: #f3f6f9;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  min-height: 46px;
  padding: 8px 12px;
}

.reserva-selecao-resumo strong {
  color: #181c32;
  font-weight: 800;
}

.reserva-selecao-resumo span {
  color: #7e8299;
  font-weight: 700;
}

.reserva-mesas-picker {
  border: 1px solid #ebedf3;
  border-radius: 6px;
  margin-top: 12px;
  overflow: hidden;
}

.reserva-mesas-map {
  background-color: #f7f8fb;
  background-image: linear-gradient(#edf0f5 1px, transparent 1px),
    linear-gradient(90deg, #edf0f5 1px, transparent 1px);
  background-size: 32px 32px;
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(auto-fill, minmax(104px, 1fr));
  max-height: 260px;
  overflow: auto;
  padding: 12px;
}

.reserva-mesa-card {
  align-items: center;
  background: #fff;
  border: 2px solid #1bc5bd;
  border-radius: 8px;
  color: #181c32;
  display: flex;
  flex-direction: column;
  min-height: 92px;
  padding: 8px;
  text-align: center;
}

.reserva-mesa-card span {
  color: #7e8299;
  font-size: 0.68rem;
  font-weight: 800;
  overflow: hidden;
  text-overflow: ellipsis;
  text-transform: uppercase;
  white-space: nowrap;
  width: 100%;
}

.reserva-mesa-card strong {
  font-size: 1.35rem;
  line-height: 1.1;
}

.reserva-mesa-card small,
.reserva-mesa-card em {
  color: #3f4254;
  font-style: normal;
  font-weight: 700;
  line-height: 1.2;
}

.reserva-mesa-card-ocupada {
  border-color: #f64e60;
}

.reserva-mesa-card-reservada,
.reserva-mesa-card-busy {
  background: #fff8dd;
  border-color: #ffa800;
}

.reserva-mesa-card-selected {
  background: #f3fbff;
  border-color: #3699ff;
  box-shadow: inset 0 0 0 2px rgba(54, 153, 255, 0.18);
}

.reserva-mesa-card-selected span,
.reserva-mesa-card-selected small,
.reserva-mesa-card-selected em {
  color: #181c32;
}

.reserva-mesa-card:disabled {
  cursor: not-allowed;
  opacity: 0.82;
}

.reservas-list-items {
  display: grid;
  gap: 8px;
  padding: 12px;
}

.reserva-row {
  align-items: center;
  background: #f7f8fb;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  display: grid;
  gap: 10px;
  grid-template-columns: minmax(0, 1.4fr) minmax(120px, 0.7fr) 120px auto;
  padding: 12px;
}

.reserva-row strong,
.reserva-row span {
  display: block;
}

.reserva-row strong {
  color: #181c32;
  font-weight: 800;
}

.reserva-row span {
  color: #7e8299;
  font-weight: 700;
}

.produto-atendimento-form .form-group {
  margin-bottom: 12px;
}

.produto-option {
  display: flex;
  flex-direction: column;
}

.produto-option span {
  color: #7e8299;
  font-size: 0.86rem;
}

.itens-atendimento {
  border: 1px solid #ebedf3;
  border-radius: 6px;
  margin-bottom: 18px;
  overflow: hidden;
}

.itens-atendimento-header {
  align-items: center;
  border-bottom: 1px solid #ebedf3;
  display: flex;
  justify-content: space-between;
  padding: 14px 16px;
}

.itens-atendimento-header h5 {
  color: #181c32;
  font-size: 1rem;
  font-weight: 700;
  margin: 0;
}

.itens-atendimento-header span {
  color: #7e8299;
  font-weight: 600;
}

.itens-atendimento-header-actions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.comandas-grid {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}

.comanda-card {
  background: #fff;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  color: #3f4254;
  display: flex;
  flex-direction: column;
  min-height: 86px;
  padding: 12px;
  text-align: left;
}

.comanda-card strong {
  color: #181c32;
  margin-bottom: 4px;
}

.comanda-card span {
  color: #7e8299;
  font-weight: 600;
}

.comanda-card-active {
  border-color: #3699ff;
  box-shadow: inset 0 0 0 1px #3699ff;
}

.atendimento-table {
  table-layout: fixed;
  width: 100%;
}

.atendimento-table th,
.atendimento-table td {
  overflow-wrap: anywhere;
  padding: 0.85rem 0.75rem;
  vertical-align: middle;
}

.atendimento-table th {
  color: #7e8299;
  font-size: 0.76rem;
  letter-spacing: 0;
  text-transform: uppercase;
  white-space: normal;
}

.atendimento-table th:nth-child(1) {
  width: 22%;
}

.atendimento-table th:nth-child(2) {
  width: 12%;
}

.atendimento-table th:nth-child(3),
.atendimento-table th:nth-child(5) {
  width: 9%;
}

.atendimento-table th:nth-child(4),
.atendimento-table th:nth-child(7) {
  width: 10%;
}

.atendimento-table th:nth-child(6) {
  width: 22%;
}

.atendimento-table th:nth-child(8) {
  width: 6%;
}

.atendimento-table .form-control {
  min-width: 0;
}

.comanda-row td {
  background: #f3f6f9;
  color: #3f4254;
  font-weight: 800;
}

.comanda-row td span {
  color: #7e8299;
  float: right;
}

.atendimento-table-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  justify-content: flex-end;
}

.atendimento-table-actions .btn {
  flex: 0 0 30px;
  height: 30px;
  width: 30px;
}

.item-cancelado {
  opacity: 0.6;
}

.historico-mesa-summary {
  background: #f3f6f9;
  border-radius: 6px;
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-bottom: 16px;
  padding: 14px 16px;
}

.historico-mesa-summary span {
  color: #7e8299;
  display: block;
  font-weight: 700;
}

.historico-mesa-summary strong {
  color: #181c32;
  display: block;
  font-size: 1.05rem;
  margin-top: 4px;
}

.atendimento-historico-table th:nth-child(1) {
  width: 24%;
}

.atendimento-historico-table th:nth-child(2) {
  width: 13%;
}

.atendimento-historico-table th:nth-child(3) {
  width: 8%;
}

.atendimento-historico-table th:nth-child(4),
.atendimento-historico-table th:nth-child(5) {
  width: 10%;
}

.atendimento-historico-table th:nth-child(6) {
  width: 12%;
}

.atendimento-historico-table th:nth-child(7) {
  width: 23%;
}

.totais-atendimento {
  align-items: center;
  background: #f3f6f9;
  border-radius: 6px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 14px 16px;
}

.totais-atendimento span {
  color: #7e8299;
  font-weight: 700;
}

.totais-atendimento strong {
  color: #181c32;
  font-size: 1.2rem;
}

.operation-summary {
  background: #f3f6f9;
  border-radius: 6px;
  color: #3f4254;
  font-weight: 600;
  margin-top: 16px;
  padding: 12px;
}

.modal-atendimento-text {
  color: #3f4254;
  font-weight: 600;
  margin-bottom: 18px;
}

.abrir-atendimento-juncao {
  align-items: end;
  background: #f3f6f9;
  border-radius: 6px;
  display: grid;
  gap: 10px;
  grid-template-columns: minmax(0, 1fr) minmax(180px, 1.2fr) auto;
  margin-top: 14px;
  padding: 12px;
}

.abrir-atendimento-juncao strong,
.abrir-atendimento-juncao span {
  display: block;
}

.abrir-atendimento-juncao strong {
  color: #181c32;
  font-weight: 800;
}

.abrir-atendimento-juncao span {
  color: #7e8299;
  font-weight: 700;
}

.modal-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 20px;
}

.comanda-avulsa-modal {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.avulsa-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.avulsas-sem-mesa-compact {
  border: 1px solid #ebedf3;
  border-radius: 6px;
  padding: 12px 14px 0;
}

.avulsa-status {
  align-items: center;
  background: #f3f6f9;
  border: 1px solid #e4e6ef;
  border-radius: 6px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: space-between;
  padding: 12px 14px;
}

.avulsa-status strong {
  color: #181c32;
  font-size: 1.05rem;
}

.comandas-operacao {
  display: grid;
  gap: 16px;
}

.comandas-operacao-toolbar {
  align-items: center;
  display: grid;
  gap: 12px;
  grid-template-columns: minmax(280px, 1fr) auto;
}

.comandas-search,
.nova-comanda-inline,
.comandas-filter-chips {
  align-items: center;
  display: flex;
  gap: 10px;
}

.comandas-search .form-control {
  max-width: 420px;
}

.comandas-filter-chips {
  flex-wrap: wrap;
  justify-content: flex-end;
}

.comandas-filter-chip {
  background: #f3f6f9;
  border: 1px solid #e4e6ef;
  border-radius: 6px;
  color: #7e8299;
  font-weight: 800;
  min-height: 34px;
  padding: 7px 12px;
}

.comandas-filter-chip.active {
  background: #181c32;
  border-color: #181c32;
  color: #fff;
}

.nova-comanda-inline {
  background: #f7f8fb;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  padding: 12px;
}

.nova-comanda-inline .form-control {
  max-width: 260px;
}

.comandas-operacao-grid {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
}

.comanda-operacao-card {
  background: #fff;
  border: 1px solid #e4e6ef;
  border-left: 5px solid #1bc5bd;
  border-radius: 6px;
  min-height: 184px;
  padding: 16px;
  text-align: left;
  transition: border-color 0.15s ease, box-shadow 0.15s ease,
    transform 0.15s ease;
}

.comanda-operacao-card:hover {
  border-color: #3699ff;
  box-shadow: 0 10px 24px rgba(24, 28, 50, 0.08);
  transform: translateY(-1px);
}

.comanda-card-top {
  display: grid;
  gap: 8px;
}

.comanda-card-top span {
  background: #c9f7f5;
  border-radius: 5px;
  color: #1bc5bd;
  font-size: 0.72rem;
  font-weight: 900;
  justify-self: start;
  padding: 5px 8px;
  text-transform: uppercase;
}

.comanda-card-top strong {
  color: #181c32;
  font-size: 1.15rem;
  line-height: 1.25;
}

.comanda-card-info {
  color: #7e8299;
  display: grid;
  font-weight: 700;
  gap: 4px;
  margin-top: 12px;
}

.comanda-card-footer {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
}

.comanda-prod-chip {
  background: #f3f6f9;
  border-radius: 5px;
  color: #3f4254;
  font-size: 0.78rem;
  font-weight: 800;
  padding: 5px 8px;
}

.comanda-prod-chip.is-production {
  background: #e1f0ff;
  color: #3699ff;
}

.comanda-prod-chip.is-ready {
  background: #e8fff3;
  color: #1bc56f;
}

.comanda-prod-chip.is-empty {
  background: #fff4de;
  color: #b97700;
}

.comanda-card-action {
  color: #3699ff;
  font-weight: 900;
  margin-left: auto;
}

.comanda-card-action i {
  margin-left: 6px;
}

@media (max-width: 767px) {
  .status-legenda {
    justify-content: flex-start;
    padding-bottom: 0;
  }

  .operacao-resumo {
    grid-template-columns: 1fr;
  }

  .entrega-queue-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .entrega-queue-actions {
    justify-content: flex-start;
  }

  .entrega-grid {
    grid-template-columns: 1fr;
  }

  .entrega-ticket-item {
    align-items: stretch;
    grid-template-columns: 1fr;
  }

  .ambiente-header {
    align-items: flex-start;
    flex-direction: column;
    gap: 6px;
  }

  .mesas-grid {
    grid-template-columns: 1fr;
  }

  .salao-map {
    min-height: 720px;
  }

  .mesa-map-card {
    min-width: 132px;
  }

  .layout-editor-toolbar {
    align-items: flex-start;
    flex-direction: column;
  }

  .layout-editor-actions {
    justify-content: flex-start;
  }

  .comandas-operacao-toolbar,
  .nova-comanda-inline {
    align-items: stretch;
    grid-template-columns: 1fr;
  }

  .comandas-search,
  .nova-comanda-inline {
    flex-direction: column;
  }

  .comandas-search .form-control,
  .nova-comanda-inline .form-control {
    max-width: none;
    width: 100%;
  }

  .produto-atendimento-form {
    padding: 12px;
  }

  /* No mobile, o seletor ocupa mais altura (modal quase full). */
  .produto-atendimento-form .seletor-produtos-garcom {
    max-height: 56vh;
  }
  .produto-atendimento-form .seletor-produtos-garcom .spg-corpo {
    max-height: calc(56vh - 110px);
  }

  .abrir-atendimento-juncao {
    align-items: stretch;
    grid-template-columns: 1fr;
  }

  .modal-atendimento-info,
  .comandas-grid,
  .historico-mesa-summary {
    grid-template-columns: 1fr;
  }

  .reserva-row {
    align-items: stretch;
    grid-template-columns: 1fr;
  }

  .atendimento-table th,
  .atendimento-table td {
    font-size: 0.82rem;
    padding: 0.65rem 0.45rem;
  }

  .atendimento-table td:nth-child(4),
  .atendimento-table th:nth-child(4),
  .atendimento-table td:nth-child(5),
  .atendimento-table th:nth-child(5) {
    display: none;
  }
}

/* Responsividade do grid de mesas: tablet usa 2 colunas, mobile estreito 1 */
@media (min-width: 576px) and (max-width: 991.98px) {
  .mesas-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 575.98px) {
  .mesas-grid {
    grid-template-columns: 1fr;
  }
}

/* Seletor rapido de visao do salao (mesas/comandas) */
.restaurante-visao-seletor {
  display: inline-flex;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.08);
}
.restaurante-visao-seletor .btn {
  border-radius: 0;
  border: none;
  padding: 0.45rem 0.9rem;
  line-height: 1.2;
}
.restaurante-visao-seletor .btn.btn-primary {
  color: #fff;
}
@media (max-width: 575.98px) {
  .restaurante-visao-seletor {
    margin-bottom: 0.5rem;
  }
}

/* Modal "Utilizar reserva?" */
.utilizar-reserva-content {
  padding: 0.25rem 0.25rem;
}
.reserva-lista .reserva-radio {
  padding: 0.5rem 0.25rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}
.reserva-unica-info {
  padding: 0.75rem 1rem;
  background: rgba(54, 153, 255, 0.06);
  border-radius: 8px;
}
</style>

<style>
.restaurante-atendimento-dialog {
  max-width: calc(100vw - 16px);
  width: calc(100vw - 16px);
}

.restaurante-atendimento-content {
  min-height: auto;
}

.restaurante-atendimento-content .modal-body {
  max-height: none;
  overflow: visible;
}

.abertura-comanda-toggle .btn {
  min-width: 0;
}

@media (max-width: 991.98px) {
  .restaurante-atendimento-dialog {
    max-width: calc(100vw - 20px);
    width: calc(100vw - 20px);
  }
}
</style>
